import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { formatTime } from '../../utils/clock';
import { useDispatch, useSelector } from 'react-redux';
import Swal from "sweetalert2";
import { toast } from 'react-toastify';
import { ArrowLeftCircle, ArrowRightCircle, MoreVertical, ArrowLeft, ArrowRight, ChevronRight, CalendarCheck, Video, FileText, MessageSquare, Send, Download, UserRound, ShieldAlert, HeartPulse } from 'lucide-react';
import {
    cancelAppointment,
    getAppointmentById,
    updateDoctorNotes,
    updatePreparationNotes,
    updateWrittenNotes,
    updateSummaryNotes,
    uploadAudio,
    uploadImage,
    deleteUploadedImage,
    deleteAudio,
    updateDoctorNotes1,
    updateAppointment,
    getRescheduleRecommendation,
    rescheduleAppointment,
    getDoctorAvailableSlots,
    getAvailableSlots,
    applyMediaResult,
    applySavedNotes
} from '../../redux/appointmentSlice';
import { getSocket } from '../../socket';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { getPatientById, getMedicalHistory } from '../../redux/patientSlice';
import { getDoctorSettings } from '../../redux/doctorSlice';
import { getFileURL } from '../../utils/fileURL';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import { useNavigate } from 'react-router-dom';
import MasterLabel from '../common/MasterLabel';
import CommonLoader from '../CommonLoader';
import { X, Wallet, CheckCircle2 } from 'lucide-react';
import { hospitalPatientCode } from '../../utils/patientCode';
import SummaryDrawer from './SummaryDrawer';
import AskAIDrawer from './AskAIDrawer';
import PrescriptionSection from './PrescriptionSection';
import HealthRecordVisitRow from '../health/HealthRecordVisitRow';
import HealthRecordDrawer from '../health/HealthRecordDrawer';
import DraftChip from '../common/DraftChip';
import useDraftAutosave, { suspendAutosave, resumeAutosave } from '../../hooks/useDraftAutosave';
import { fetchDrafts, commitDrafts, noteDraftSection } from '../../redux/appointmentDraftSlice';
import MeetingSummary from '../MeetingSummary';
import AppointmentSummary from '../AppointmentSummary';
import { getPrescriptionFields, updateAppointmentCustomNote } from '../../redux/prescriptionFieldSlice';
import CompletedPaymentCard from '../payment/CompletedPaymentCard';
import { getPaymentOptions, payAppointment } from '../../redux/paymentSlice';
import { currencySymbol } from '../../utils/currency';
import { serverReason, cleanClinicalText } from './prescriptionHelpers';
import { emptyHistory as emptymedicalhistory, consultation as medicalhistory } from '../../assets/img/illustrations';
import ClinicalHiddenNote from '../common/ClinicalHiddenNote';
import { isColleagueVisit, canManageVisitMedia } from '../../utils/visitAccess';
import CheckReadingsBlock from '../checks/CheckReadingsBlock';
import VisitReadingsPanel from '../checks/VisitReadingsPanel';
import ProgrammeCard from '../programmes/ProgrammeCard';
import { AppointmentProgrammeTag } from '../programmes/ProgrammeStopLine';
import usePlanControl from '../../hooks/usePlanControl';
import useVisitAiGates from '../../hooks/useVisitAiGates';
import { audioClipState, clipAwaitsTranscript } from '../../utils/visitTranscription';

// The visit sets still missing (REQUIRED_CHECKS_MISSING / requiredMissing) as
// a short HTML list for the warning dialog. Every name is escaped: set and
// field names are typed by people.
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

// Every value that has ever meant "paid" across the codebase (current + legacy).
const PAID_VALUES = ['Paid', 'paid', 'collected', 'Completed'];
const isPaidStatus = (s) => PAID_VALUES.includes(s);

// The complete-appointment invoice offers ONLY the methods GET /payment/options
// returns for this doctor (minus the gateway ones, which a doctor cannot
// "collect" at the desk). There is no client-side fallback list: an empty list
// is the server's decision — the doctor accepts nothing, the hospital switched
// a method off, a platform hold — and inventing Cash / Card / Online there is
// how a visit got recorded as paid online with no money moved.
// No "pay later" — an appointment must be settled before it can be completed.
//
// The PATIENT's own "Pay" invoice follows the same rule (owner, 15 Sep): it
// used to fall back to Card and Online whenever the list came back empty, and
// the server now refuses exactly those (PAYMENT_METHOD_NOT_OFFERED, and
// ONLINE_PAYMENT_NEEDS_GATEWAY for 'online' with no gateway — nothing would
// be charged). So a patient is offered only what the server returned, minus
// cash (collected at the hospital) and pay-later, and minus any gateway method
// — `requiresGateway`, or 'online' itself — unless `gatewayReady` is true.
const patientPayMethods = (options) => (options?.methods || []).filter((m) => {
    if (!m || typeof m.key !== 'string') return false;
    if (m.key === 'cash' || m.key === 'paylater') return false;
    if (m.requiresGateway || m.key === 'online') return options?.gatewayReady === true;
    return true;
});

// Maps a seeded note field's legacyKey back to the appointment column it
// used to live in, so existing data still renders even though new saves now
// go to customNotes.
const LEGACY_KEY_TO_COLUMN = {
    communication_therapist_patient: 'description',
    notes_of_doctor: 'doctorDescription',
    preparation: 'preparationNotes',
    consultation_notes: 'writtenNotes',
    summary: 'summaryNotes',
};

// Utility function to format localTime to date and time
const formatDateTime = (localTime) => {
    if (!localTime) return { date: '', time: '' };
    const dateObj = new Date(localTime);
    const date = dateObj.toLocaleDateString('en-CA'); // Format as YYYY-MM-DD
    const time = formatTime(dateObj); // "2:30 PM" — the one clock format
    return { date, time };
};

// Maximum length constants
const MAX_NOTES_LENGTH = 2000;
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

// A stored visit note is normally a plain string keyed by fieldId. Read an
// object form too ({ value, label } / { text, fieldName } …) so a note that
// carries its own section name can be titled even after that section is gone.
const readStoredNote = (raw) => {
    if (raw === null || raw === undefined) return { text: '', label: '' };
    if (typeof raw === 'string') return { text: raw, label: '' };
    if (typeof raw === 'object') {
        const text = [raw.value, raw.text, raw.note].find((v) => typeof v === 'string') || '';
        const label = [raw.label, raw.fieldName, raw.name, raw.title].find((v) => typeof v === 'string' && v.trim()) || '';
        return { text, label };
    }
    return { text: String(raw), label: '' };
};

// Validate notes textarea. Every printable character is allowed — doses, BP,
// dates, lab values — except < and > (see cleanClinicalText).
const validateNotesInput = (value = '', t) => {
    const sanitized = cleanClinicalText(value);

    // Check max length
    if (sanitized.length > MAX_NOTES_LENGTH) {
        return t ? t('medical_history.notes_exceed_limit', { max: MAX_NOTES_LENGTH }) : `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`;
    }

    // Check for only whitespace (allow empty)
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
        return t ? t('medical_history.notes_only_spaces') : 'Notes cannot contain only spaces';
    }

    return '';
};

// The sign in front of a visit's price — the shared helper (P24), which reads
// the currency CODE of the object the API sends (₹ for INR) and every legacy
// shape, instead of a local INR/EUR comparison.
const getCurrencySymbol = (currency) => currencySymbol(currency);

// initialTab     the status tab to open on (the calendar opens a Completed
//                visit on 'Completed'); defaults to 'Booked'.
// leaveGuardRef  set to { requestLeave(proceed) }: the host (the calendar's
//                "Back to calendar") asks the SAME "Unsaved changes" dialog
//                before leaving the visit; `proceed` runs when leaving is OK.
const STATUS_TABS = ['Booked', 'Rescheduling', 'Completed', 'Cancelled', 'NoShow'];
const MedicalHistory = ({ selected, isOpenAppointmentMode = false, initialTab = 'Booked', leaveGuardRef }) => {
    const { t, i18n } = useTranslation(['medical_history']);
    const {
        selectedPatient,
        isLoading: patientLoading,
        error: patientError,
        medicalHistoryData,
        medicalHistoryPage,
        medicalHistoryTotalPages,
        medicalHistoryLoading
    } = useSelector((state) => state.patient);
    const { selectedAppointment, isAppointmentLoading: appointmentLoading, error: appointmentError, availableSlots, availableDoctorSlots } = useSelector((state) => state.appointment);
    const completePaymentOptions = useSelector((state) => state.payment?.paymentOptions);
    const paymentOptionsStatus = useSelector((state) => state.payment?.paymentOptionsStatus);
    const paymentOptionsError = useSelector((state) => state.payment?.error);
    const { selectedDoctor, doctorSettings } = useSelector((state) => state.doctor);
    const {
        fields: prescriptionFields,
        fieldsFor: prescriptionFieldsFor,
        loading: prescriptionFieldsLoading,
        loadError: prescriptionFieldsLoadError,
    } = useSelector((state) => state.prescriptionField);
    const menuRef = useRef(null);
    const scrollObserverRef = useRef(null);
    const cardsContainerRef = useRef(null);
    const userRole = localStorage.getItem('Role');
    // For a doctor, the business doctorId (the same value getMedicalHistory
    // sends as ?doctorId=). Decides "my visit" versus a colleague's below.
    const myId = localStorage.getItem('ID');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // The visit's three AI questions (owner, 2 Oct 2026): is a clip
    // transcribed, is a recorded video visit transcribed, is a summary made.
    // Off means nothing is "generating" — no spinner, no poll; what is stored
    // still shows, and the audio upload is offered either way.
    const aiGates = useVisitAiGates();

    // Tracks the open appointment for socket handlers (refs avoid re-subscribing
    // the socket on every selection change).
    const selectedAppointmentIdRef = useRef(null);
    useEffect(() => {
        selectedAppointmentIdRef.current = selectedAppointment?.appointmentId || null;
    }, [selectedAppointment?.appointmentId]);

    // Live async results: the backend pushes transcript:ready / ocr:ready over
    // Socket.IO when a worker finishes. Patch the matching upload item in redux
    // so "Generating…" is replaced by the real text without a refresh. The
    // socket may not be connected yet when this mounts, so retry attaching
    // (mirrors MeetingSummary/AppointmentSummary's pattern).
    useEffect(() => {
        let alive = true;
        let retryTimer = null;
        let attachedSocket = null;
        const onTranscript = ({ appointmentId, uploadId, transcribedNotes, status }) => {
            dispatch(applyMediaResult({ appointmentId, uploadId, kind: 'audio', text: transcribedNotes, status }));
        };
        const onOcr = ({ appointmentId, uploadId, ocrNotes, status }) => {
            dispatch(applyMediaResult({ appointmentId, uploadId, kind: 'image', text: ocrNotes, status }));
        };
        // Doctor confirmed the meeting is completed → refetch the open appointment
        // so the Connect Meet button disappears instantly (no refresh).
        const onMeetingCompleted = ({ appointmentId }) => {
            if (appointmentId && appointmentId === selectedAppointmentIdRef.current) {
                dispatch(getAppointmentById(appointmentId));
            }
        };
        const attach = () => {
            if (!alive) return;
            const socket = getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attachedSocket = socket;
            socket.on('transcript:ready', onTranscript);
            socket.on('ocr:ready', onOcr);
            socket.on('meeting:completed', onMeetingCompleted);
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attachedSocket) {
                attachedSocket.off('transcript:ready', onTranscript);
                attachedSocket.off('ocr:ready', onOcr);
                attachedSocket.off('meeting:completed', onMeetingCompleted);
            }
        };
    }, [dispatch]);

    // Fallback for lost socket events (tab backgrounded, reconnect mid-emit,
    // etc.): while any audio/image upload on the open appointment is still
    // 'processing', poll its status directly (bypassing the getAppointmentById
    // thunk so isAppointmentLoading isn't toggled and inputs don't flicker
    // disabled) and patch redux the same way the socket handler does.
    const axiosInstance = useAxiosAuth();
    useEffect(() => {
        const appointmentId = selectedAppointment?.appointmentId;
        // A clip stored without a transcript is waiting on nothing.
        const hasProcessing = (selectedAppointment?.audioUploads || []).some((u) => clipAwaitsTranscript(u, aiGates.audioTranscription))
            || (selectedAppointment?.imageUploads || []).some((u) => u.status === 'processing');
        if (!appointmentId || !hasProcessing) return undefined;

        let alive = true;
        const timer = setTimeout(async () => {
            if (!alive) return;
            try {
                const res = await axiosInstance.get(`${baseURL}/appointment/${appointmentId}`);
                const fresh = res.data?.data || res.data;
                (fresh?.audioUploads || []).forEach((u) => {
                    if (u.status !== 'processing') {
                        dispatch(applyMediaResult({ appointmentId, uploadId: u.uploadId, kind: 'audio', text: u.transcribedNotes, status: u.status }));
                    }
                });
                (fresh?.imageUploads || []).forEach((u) => {
                    if (u.status !== 'processing') {
                        dispatch(applyMediaResult({ appointmentId, uploadId: u.uploadId, kind: 'image', text: u.ocrNotes, status: u.status }));
                    }
                });
            } catch {
                // silent — next tick (or the socket) will catch up
            }
        }, 6000);
        return () => { alive = false; clearTimeout(timer); };
        // Re-runs whenever an upload's status/text changes (including after this
        // poll patches one), so it keeps ticking every ~6s until none are 'processing'.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedAppointment?.appointmentId, selectedAppointment?.audioUploads, selectedAppointment?.imageUploads, aiGates.audioTranscription]);

    // Render an async transcript/OCR result, or a "Generating…" spinner while
    // the worker is still processing it.
    const mediaTextBlock = (text, status, generatingLabel) => {
        // The worker reports a miss the same way it reports success, with
        // status 'failed' and no text (services/mediaResultConsumer.js). Saying
        // nothing here is what left the doctor on a spinner that had already
        // stopped: they could not tell "still working" from "never arrived".
        if (status === 'failed') {
            return (
                <p className="mt-2 w-full p-3 border border-amber-200 rounded-xl bg-amber-50 text-sm text-amber-700">
                    {t('medical_history.media_failed', 'This did not come back from the transcription service. Upload it again to retry.')}
                </p>
            );
        }
        if (status === 'processing') {
            return (
                <p className="mt-2 w-full p-3 border border-gray-200 rounded bg-gray-50 text-sm text-gray-400 italic flex items-center gap-2">
                    <span className="h-3 w-3 border-2 border-gray-300 border-t-[#4ABDBD] rounded-full animate-spin inline-block" />
                    {generatingLabel}
                </p>
            );
        }
        if (!text) return null;
        return (
            <p className="mt-2 w-full p-3 border border-gray-300 rounded bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                {text}
            </p>
        );
    };

    // One prescription audio clip's transcript line. Where no transcript will
    // come (ai_transcription off for the hospital or the doctor's plan, or the
    // server stored the clip with `transcription: 'off'`) it says the audio is
    // saved — never "Generating…" (owner, 2 Oct 2026).
    const audioTextBlock = (upload, key) => {
        if (audioClipState(upload, aiGates.audioTranscription) === 'off') {
            return (
                <p data-testid={`patient-medical-history-audio-no-transcription-${key}`} className="mt-2 text-xs text-gray-500">
                    {t('planGate.audio_saved_no_transcription', 'Transcription is not included — the audio is saved.')}
                </p>
            );
        }
        return mediaTextBlock(upload.transcribedNotes, upload.status, t('medical_history.generating_transcript', 'Generating transcript…'));
    };

    // State management
    const [selectedTab, setSelectedTab] = useState(() => (STATUS_TABS.includes(initialTab) ? initialTab : 'Booked'));
    const [isSummaryDrawerOpen, setIsSummaryDrawerOpen] = useState(false);
    const [isAskAIDrawerOpen, setIsAskAIDrawerOpen] = useState(false);
    const [isOpenDetails, setIsOpenDetails] = useState(false);
    // Tracks which inline note textareas are expanded (taller). Keyed by `section.section`.
    const [expandedSections, setExpandedSections] = useState({});
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // 'complete' = doctor collects payment + completes the appointment.
    // 'pay'      = patient settles online/card before the appointment is completed.
    const [paymentModalMode, setPaymentModalMode] = useState('complete');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [selectedCard, setSelectedCard] = useState({
        speciality: '',
        type: '',
        color: '#000000',
        date: '',
        time: '',
        appointmentId: ''
    });
    const [showDetails, setShowDetails] = useState(false);
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [audioPreview, setAudioPreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    // Stores only the uploadId (or 'legacy'), not the upload object — so the
    // detail view below always re-derives the live item from redux instead of
    // a frozen snapshot, and picks up OCR results/status as they arrive.
    const [viewingImageUpload, setViewingImageUpload] = useState(null);
    const viewingImageUploadLive = useMemo(() => {
        if (!viewingImageUpload) return null;
        if (viewingImageUpload === 'legacy') {
            return { uploadId: 'legacy', imageUrl: selectedAppointment?.imageUrl, ocrNotes: selectedAppointment?.ocrNotes, status: 'done' };
        }
        return (selectedAppointment?.imageUploads || []).find((u) => u.uploadId === viewingImageUpload) || null;
    }, [viewingImageUpload, selectedAppointment]);
    const scrollPositionRef = useRef(0);
    const shouldRestoreScrollRef = useRef(false);
    const [isCardLoading, setIsCardLoading] = useState(false);
    // Why the open card's details are NOT shown, when they are not:
    //   { kind: 'colleague' }            a doctor opened another doctor's visit —
    //                                    GET /appointment/:id is 403 for them
    //                                    (R3), so it is never asked for;
    //   { kind: 'refused', message }     the server refused the read anyway (a
    //                                    staff login without appointments.view,
    //                                    a visit that moved) — its own sentence.
    // Without this the panel kept rendering the PREVIOUS card's appointment,
    // which is still in redux when a read is refused.
    const [visitNotice, setVisitNotice] = useState(null);
    const [savingField, setSavingField] = useState(null);
    const isSaving = savingField !== null;
    // Doctor-configured visibility for the four built-in feature blocks lives
    // on doctorSettings.prescriptionSections; default to all-on if absent.
    const visibleStaticTypes = useMemo(() => {
        const config = doctorSettings?.prescriptionSections || {
            medicines: true, audio: true, image: true,
        };
        const set = new Set();
        Object.entries(config).forEach(([k, v]) => { if (v) set.add(k); });
        return set;
    }, [doctorSettings]);
    // The doctor's own "Check sets" switch when this screen already holds the
    // doctor's settings (prescriptionSections.checkSets, absent = off);
    // null = not known here, and the visit's readings answer decides.
    const myCheckSwitch = userRole === 'doctor' && doctorSettings && String(doctorSettings.doctorId) === String(myId)
        ? doctorSettings?.prescriptionSections?.checkSets === true
        : null;
    const visibleNoteFields = useMemo(
        () => prescriptionFields.filter((f) => f.visible !== false),
        [prescriptionFields]
    );
    const [notesErrors, setNotesErrors] = useState({});
    // "Save & complete" is committing the visit's drafts (one request).
    const [committing, setCommitting] = useState(false);
    const [audioError, setAudioError] = useState('');
    const [imageError, setImageError] = useState('');
    const originalNotesRef = useRef({});
    // Which visit `notes` / `originalNotesRef` currently describe.
    const notesVisitRef = useRef(null);
    const pendingNavigationRef = useRef(null);
    const [notes, setNotes] = useState({});
    const [hasCalledHandleCardClick, setHasCalledHandleCardClick] = useState(false); // Track if handleCardClick was called
    const previousPatientIdRef = useRef(null); // Track previous patientId to detect changes
    const previousTabRef = useRef(null); // Track previous tab to detect changes
    const hasInitialFetchRef = useRef(false); // Track if initial fetch has been completed
    const isInitialMountRef = useRef(true); // Track if this is the initial mount
    const isFetchingRef = useRef(false); // Track if API call is in progress to prevent concurrent calls
    const [currentRecommendation, setCurrentRecommendation] = useState(null); // Store current reschedule recommendation data
    const [isViewingMoreSlots, setIsViewingMoreSlots] = useState(false); // Track if viewing all available slots
    const [weekOffsetMore, setWeekOffsetMore] = useState(0); // Week offset for view more slots
    const [openDayMore, setOpenDayMore] = useState(null); // Opened day for view more slots
    const [isSwitchingTab, setIsSwitchingTab] = useState(false); // Track if user is switching tabs
    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get system timezone for reschedule
    const detailsContainerRef = useRef(null); // Ref for scrolling in medical history details

    // Is anything on the open visit only a draft? EXACTLY what "Save and
    // continue" would write (commitVisitDrafts): a note box whose text differs
    // from its saved note and is not blank, or prescription lines still
    // pending. It used to count ANY difference — a box holding only a space,
    // or a cleared one, which the commit cannot write — so after a successful
    // "Save and continue" the next leave asked again although nothing had
    // changed since (owner, 24 Sep 2026). Asked about the same thing it
    // commits, a commit that landed always answers "nothing unsaved".
    //
    // ASK ONCE (owner, 24 Sep 2026). Once the doctor has DECIDED about what is
    // on screen — "Keep as draft", the route guard's "Keep as draft and
    // leave", or a commit that landed — that content is the ACKNOWLEDGED
    // draft, and the dialog does not ask about it again: a section counts only
    // when it differs from its saved value AND from its acknowledged content
    // (notes: the text; prescription: the pending lines, serialised). Typing
    // again makes it news. The baseline belongs to the open visit and is
    // dropped when another visit is opened.
    const acknowledgedRef = useRef({ visitId: null, notes: {}, rx: null, ck: null });
    const acknowledgeDrafts = () => {
        acknowledgedRef.current = {
            visitId: notesVisitRef.current,
            notes: { ...notes },
            rx: prescriptionDraftRef.current?.pendingKey?.() ?? null,
            ck: checksDraftRef.current?.pendingKey?.() ?? null,
        };
    };
    const hasUnsavedChanges = () => {
        const original = originalNotesRef.current;
        const ack = acknowledgedRef.current.visitId && acknowledgedRef.current.visitId === notesVisitRef.current
            ? acknowledgedRef.current : null;
        const undecidedNotes = draftNoteSections().filter((section) => !ack || ack.notes[section] !== notes[section]);
        const notesPending = !!original && Object.keys(original).length > 0 && undecidedNotes.length > 0;
        const rx = prescriptionDraftRef.current;
        const rxPending = !!rx?.hasPendingDraft?.() && !(ack && ack.rx !== null && ack.rx === rx.pendingKey?.());
        // Check readings typed in the Check readings block (check sets) —
        // the same "ask once" rule, on the pending sets serialised.
        const ck = checksDraftRef.current;
        const ckPending = !!ck?.hasPendingDraft?.() && !(ack && ack.ck !== null && ack.ck === ck.pendingKey?.());
        return notesPending || rxPending || ckPending;
    };

    // Leaving a visit (another card, another tab, the back arrow, Cancel on a
    // completed visit's edit) while a note box is only a draft (owner, 24 Sep
    // 2026):
    //   "Save and continue"  the SAME single commit "Save & complete" uses
    //                        (commitVisitDrafts): one request, one toast; a
    //                        refusal is the server's sentence and we STAY.
    //   "Keep as draft"      the server drafts are left as they are (no DELETE,
    //                        no revert); the pending debounced write is sent
    //                        now so the last keystrokes are in the draft.
    //   "Stay"               nothing.
    const confirmUnsavedChanges = async (onContinue) => {
        const result = await Swal.fire({
            title: t('medical_history.unsaved_changes_title', 'Unsaved changes'),
            text: t('medical_history.unsaved_changes_text', 'Some of what you typed is only a draft.'),
            icon: 'warning',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('medical_history.save_and_continue', 'Save and continue'),
            denyButtonText: t('medical_history.keep_as_draft', 'Keep as draft'),
            cancelButtonText: t('medical_history.stay', 'Stay'),
            confirmButtonColor: '#4ABDBD',
            denyButtonColor: '#6b7280',
            cancelButtonColor: '#9ca3af',
            allowOutsideClick: false,
        });

        if (result?.isConfirmed) {
            const outcome = await commitVisitDrafts({
                blockedMessage: t('medical_history.unsaved_blocked', 'Not everything could be saved, so you are still on this visit.'),
            });
            if (outcome === 'committed' || outcome === 'nothing') {
                acknowledgeDrafts();
                if (onContinue) onContinue();
            }
        } else if (result?.isDenied) {
            noteDrafts.flushAll();
            prescriptionDraftRef.current?.flushDraft?.();
            checksDraftRef.current?.flushDraft?.();
            acknowledgeDrafts();
            if (onContinue) onContinue();
        }
        // Stay — do nothing.
    };

    // Doctor-only: when true, the Completed tab right-section becomes fully editable
    const [isEditingCompleted, setIsEditingCompleted] = useState(false);

    // Reset edit mode whenever the user switches tabs or selects a different appointment
    useEffect(() => {
        setIsEditingCompleted(false);
    }, [selectedTab, selectedCard?.appointmentId]);

    // Determine if inputs should be disabled
    const isInputDisabled = () => {
        if (isSaving || patientLoading || appointmentLoading) {
            return true;
        }
        if (userRole === 'patient' || userRole === 'admin') {
            return true;
        }
        if (userRole === 'doctor') {
            if (selectedTab === 'Booked') return false;
            if (selectedTab === 'Completed') return !isEditingCompleted;
            return true;
        }
        // Staff (role 'user') and any other login write nothing here: the visit
        // notes and media belong to the visit's doctor and the hospital admin
        // (D-5 / R5), and the server refuses everyone else. This used to fall
        // through to "enabled".
        return true;
    };

    // Recordings and images: the visit's OWN doctor or the hospital's admin
    // (R5). A colleague doctor and staff see the clips and scans read-only.
    const canManageMedia = canManageVisitMedia(selectedAppointment, userRole, myId);
    const isMediaDisabled = () => !canManageMedia || isInputDisabled();
    // 1 Oct 2026 (owner): an image is read by the AI, and the server refuses
    // the upload (403 PLAN_FEATURE_MISSING) when the DOCTOR's plan lacks
    // ai_ocr. The doctor gets no image box then — one quiet line instead; the
    // scans already on the visit stay. usePlanControl is true for anyone not a
    // doctor (the server judges them).
    // 2 Oct 2026 (owner): the AUDIO upload is no longer gated — without
    // ai_transcription the clip is stored and playable, just not transcribed
    // (aiGates.audioTranscription decides what is drawn under it).
    const ocrPlanOn = usePlanControl('ai_ocr');
    const summaryPlanOn = usePlanControl('ai_appointment_summary');

    // Disable a save button only when its own save is in flight (or due to
    // permission/page-load reasons). This prevents every save button from
    // appearing "loading" when one is clicked.
    const isSaveButtonDisabled = (fieldKey) => {
        if (patientLoading || appointmentLoading) return true;
        if (savingField !== null && savingField !== fieldKey) return false;
        if (savingField === fieldKey) return true;
        if (userRole === 'patient' || userRole === 'admin') return true;
        if (userRole === 'doctor') {
            if (selectedTab === 'Booked') return false;
            if (selectedTab === 'Completed') return !isEditingCompleted;
            return true;
        }
        // Same as isInputDisabled: nobody but the doctor and the admin saves here.
        return true;
    };

    // Fetch medical history when patientId becomes available or when tab changes
    useEffect(() => {
        // Get patientId from selectedPatient or localStorage (for patient role)
        const patientId = selectedPatient?.patientId || (userRole === 'patient' ? localStorage.getItem('ID') : null);

        // Skip if already fetching to prevent concurrent calls
        if (isFetchingRef.current) {
            console.log('MedicalHistory - Skipping: API call already in progress');
            return;
        }

        // Check if patientId or tab has changed
        const patientIdChanged = patientId && patientId !== previousPatientIdRef.current;
        const tabChanged = previousTabRef.current !== null && selectedTab !== previousTabRef.current;

        // Determine if we should fetch:
        // 1. On initial mount: only if patientId is available and we haven't fetched yet
        // 2. After initial mount: only if patientId changed (from null to value) or tab actually changed
        const shouldFetch = patientId && (
            (isInitialMountRef.current && !hasInitialFetchRef.current) || // Initial mount with patientId available
            (!isInitialMountRef.current && patientIdChanged) || // PatientId became available after mount
            (!isInitialMountRef.current && tabChanged) // Tab changed after initial mount
        );

        if (shouldFetch) {
            console.log('MedicalHistory - Calling getMedicalHistory API with patientId:', patientId, 'status:', selectedTab, 'isInitialMount:', isInitialMountRef.current);

            // Update refs synchronously BEFORE dispatch to prevent duplicate calls
            if (patientId) {
                previousPatientIdRef.current = patientId;
            }
            previousTabRef.current = selectedTab;
            hasInitialFetchRef.current = true;
            isInitialMountRef.current = false;
            isFetchingRef.current = true; // Set flag to prevent concurrent calls

            // Fetch medical history when patient becomes available or tab changes
            dispatch(getMedicalHistory({
                patientId: patientId,
                status: selectedTab,
                page: 1,
                limit: 5
            })).finally(() => {
                isFetchingRef.current = false; // Reset flag after API call completes
                setIsSwitchingTab(false);
                console.log('MedicalHistory - API call completed');
            });
        }
    }, [selectedPatient?.patientId, selectedTab, dispatch, userRole]);

    // Initial call to handleCardClick if selected has data
    useEffect(() => {
        if (selected && !hasCalledHandleCardClick && Object.keys(selected).length > 0) {
            handleCardClick(selected);
            setHasCalledHandleCardClick(true); // Prevent further calls
        }
    }, [selected, hasCalledHandleCardClick]);

    // Infinite scroll implementation
    useEffect(() => {
        if (!scrollObserverRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                const patientId = selectedPatient?.patientId;
                if (
                    entry.isIntersecting &&
                    !medicalHistoryLoading &&
                    medicalHistoryPage < medicalHistoryTotalPages &&
                    patientId
                ) {
                    // Load next page with limit 5
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: selectedTab,
                        page: medicalHistoryPage + 1,
                        limit: 5
                    }));
                }
            },
            {
                root: null, // Use viewport as root for better mobile experience
                rootMargin: '200px', // Start loading 200px before reaching the bottom
                threshold: 0.1
            }
        );

        const currentObserverRef = scrollObserverRef.current;
        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
        };
    }, [medicalHistoryLoading, medicalHistoryPage, medicalHistoryTotalPages, selectedTab, selectedPatient?.patientId, dispatch]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuIndex(null);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (audioPreview) URL.revokeObjectURL(audioPreview);
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [audioPreview, imagePreview]);

    // Fetch the doctor's configured prescription fields whenever the appointment
    // (and therefore the owning doctor) changes. The right-section renders these.
    useEffect(() => {
        const doctorId = selectedAppointment?.doctorId;
        if (doctorId) {
            dispatch(getPrescriptionFields(doctorId));
        }
    }, [selectedAppointment?.doctorId, dispatch]);

    useEffect(() => {
        if (!selectedAppointment) return;
        // Build a notes object keyed by every configured 'note' field. Read
        // from customNotes first, then fall back to the legacy appointment
        // column for the five seeded fields so historical data still appears.
        const next = {};
        prescriptionFields
            .filter((f) => f.kind !== 'static')
            .forEach((f) => {
                const stored = readStoredNote(selectedAppointment.customNotes?.[f.fieldId]).text;
                if (stored !== '') {
                    next[f.fieldId] = stored;
                    return;
                }
                const legacyCol = LEGACY_KEY_TO_COLUMN[f.legacyKey];
                next[f.fieldId] = legacyCol ? (selectedAppointment[legacyCol] || '') : '';
            });
        // The SAME visit re-read (a socket push when a transcript or OCR
        // lands, a payment refresh) must not wipe what the doctor is typing:
        // a box whose text differs from what was saved keeps its text. A
        // different visit starts from its own saved notes.
        const sameVisit = notesVisitRef.current === selectedAppointment.appointmentId;
        const previousSaved = originalNotesRef.current || {};
        notesVisitRef.current = selectedAppointment.appointmentId;
        originalNotesRef.current = { ...next };
        // Another visit: nothing on it has been decided about yet.
        if (!sameVisit) acknowledgedRef.current = { visitId: null, notes: {}, rx: null };
        setNotes((prev) => {
            if (!sameVisit) return next;
            const merged = { ...next };
            Object.keys(prev).forEach((k) => {
                if (k in merged && prev[k] !== (previousSaved[k] || '')) merged[k] = prev[k];
            });
            return merged;
        });
    }, [selectedAppointment, prescriptionFields]);

    // Every note written on the open visit, for the Completed view. A completed
    // visit is a record: it shows the notes it has, whatever the doctor's
    // template says today. Fields still in the template (visible OR hidden) are
    // titled and ordered by it; a note under a section since deleted is titled
    // by the label the stored note carries, or a neutral heading when it
    // carries none. Deleting a section only removes it from NEW visits.
    const fieldsReadyForVisit = !!selectedAppointment?.doctorId
        && prescriptionFieldsFor === selectedAppointment.doctorId
        && !prescriptionFieldsLoading;
    const completedNoteBlocks = useMemo(() => {
        if (!selectedAppointment) return [];
        const blocks = [];
        const known = new Set();
        prescriptionFields
            .filter((f) => f.kind !== 'static')
            .forEach((f) => {
                known.add(f.fieldId);
                const value = notes[f.fieldId];
                if (value) blocks.push({ key: f.fieldId, title: f.name, value, inTemplate: f.visible !== false });
            });
        // Until this doctor's fields have answered, an unknown key may simply
        // be a field that has not loaded yet — don't title it "Other notes".
        if (!fieldsReadyForVisit) return blocks;
        Object.entries(selectedAppointment.customNotes || {}).forEach(([fieldId, raw]) => {
            if (known.has(fieldId)) return;
            const { text, label } = readStoredNote(raw);
            if (!text) return;
            blocks.push({
                key: fieldId,
                title: label || t('medical_history.other_notes_heading', 'Other notes'),
                value: text,
                inTemplate: false,
            });
        });
        return blocks;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAppointment, prescriptionFields, notes, fieldsReadyForVisit, i18n.language]);

    const isWithinTwoHoursBeforeAppointment = () => {
        // If early open restriction is disabled, always allow
        const restriction = selectedDoctor?.doctorSettings?.earlyOpenRestriction ?? doctorSettings?.earlyOpenRestriction;
        if (restriction === false) return true;

        const duration = selectedDoctor?.doctorSettings?.duration || doctorSettings?.duration;
        if (!selectedAppointment?.localTime || !duration) return false;

        const appointmentTime = new Date(selectedAppointment.localTime);
        const currentTime = new Date();
        const twoHoursBeforeAppointment = new Date(appointmentTime.getTime() - (duration * 60 * 1000));

        return currentTime >= twoHoursBeforeAppointment;
    };

    // ---- VISIT DRAFTS (round 9) -------------------------------------------
    // What a doctor types into a note box is saved as a server-side draft 2 s
    // after they stop typing, so looking at the patient's record, switching
    // card or closing the laptop never costs them the text. Drafts belong to
    // the doctor who owns the visit (the endpoint refuses anyone else), and a
    // draft is restored only into a box the doctor can actually edit — a
    // restored draft on a read-only completed visit would read as a saved note.
    const draftVisitId = selectedAppointment?.appointmentId || null;
    const ownsVisit = userRole === 'doctor'
        && !!selectedAppointment?.doctorId
        && String(selectedAppointment.doctorId) === String(myId)
        && selectedAppointment.clinicalHidden !== true;
    const draftsState = useSelector((state) => state.appointmentDraft);
    const noteDrafts = useDraftAutosave({ appointmentId: ownsVisit ? draftVisitId : null, enabled: ownsVisit, onKeep: acknowledgeDrafts });

    // The host's own way out of the visit (the calendar's "Back to
    // calendar") asks the same dialog as switching card or tab.
    useImperativeHandle(leaveGuardRef, () => ({
        requestLeave: async (proceed) => {
            if (hasUnsavedChanges()) {
                await confirmUnsavedChanges(proceed);
                return;
            }
            proceed();
        },
    }));
    const notesEditable = ownsVisit && showDetails && !visitNotice && !isCardLoading
        && ((selectedTab === 'Booked' && isWithinTwoHoursBeforeAppointment())
            || (selectedTab === 'Completed' && isEditingCompleted));
    const restoredForRef = useRef(null);

    useEffect(() => {
        if (ownsVisit && draftVisitId) dispatch(fetchDrafts({ appointmentId: draftVisitId }));
    }, [ownsVisit, draftVisitId, dispatch]);

    useEffect(() => {
        if (!notesEditable) {
            // Leaving edit mode re-arms the restore for the next time it opens.
            restoredForRef.current = null;
            return;
        }
        if (!fieldsReadyForVisit || notesVisitRef.current !== draftVisitId) return;
        if (!draftsState?.loaded || draftsState.appointmentId !== draftVisitId) return;
        if (restoredForRef.current === draftVisitId) return;
        restoredForRef.current = draftVisitId;
        const saved = originalNotesRef.current || {};
        const restore = visibleNoteFields
            .map((f) => ({ fieldId: f.fieldId, draft: draftsState.drafts?.[noteDraftSection(f.fieldId)] }))
            .filter(({ fieldId, draft }) => draft && typeof draft.content === 'string' && draft.content !== (saved[fieldId] || ''));
        if (restore.length === 0) return;
        setNotes((prev) => {
            const next = { ...prev };
            restore.forEach(({ fieldId, draft }) => {
                next[fieldId] = cleanClinicalText(draft.content).slice(0, MAX_NOTES_LENGTH);
            });
            return next;
        });
        restore.forEach(({ fieldId, draft }) => noteDrafts.markRestored(noteDraftSection(fieldId), draft.updatedAt));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notesEditable, fieldsReadyForVisit, draftVisitId, draftsState, visibleNoteFields]);

    // "Discard draft": the draft goes, and the box goes back to what is saved.
    const discardNoteDraft = (fieldId) => {
        noteDrafts.discard(noteDraftSection(fieldId));
        setNotes((prev) => ({ ...prev, [fieldId]: originalNotesRef.current?.[fieldId] || '' }));
        setNotesErrors((prev) => ({ ...prev, [fieldId]: '' }));
    };

    // The patient's full health record, read INSIDE the visit (a drawer over
    // it) — never by leaving it. Closed whenever the visit changes.
    const [isHealthDrawerOpen, setIsHealthDrawerOpen] = useState(false);
    useEffect(() => { setIsHealthDrawerOpen(false); }, [draftVisitId]);
    // 1 Oct 2026 (owner): a DOCTOR reading a patient's cross-hospital record
    // needs visit_sharing in their plan (the server refuses the reads). The
    // button, the compact row and the drawer all stand down without it —
    // `healthPatientId` null is the one switch they already share.
    const visitSharingPlanOn = usePlanControl('visit_sharing');
    const healthPatientId = visitSharingPlanOn
        ? (selectedPatient?.patientId || selectedAppointment?.patientId || null)
        : null;

    // Helper function to get the Monday of the current week
    const getCurrentWeekMonday = (date) => {
        const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.getFullYear(), date.getMonth(), diff);
    };

    // Helper function to scroll to bottom of details section
    const scrollDetailsToBottom = () => {
        setTimeout(() => {
            if (detailsContainerRef.current) {
                detailsContainerRef.current.scrollTop = detailsContainerRef.current.scrollHeight;
            }
        }, 0);
    };

    // Handle View More Slots button click
    const handleViewMoreSlotsInHistory = (recommendation) => {
        setIsViewingMoreSlots(true);
        setCurrentRecommendation(recommendation);
        setWeekOffsetMore(0);
        setOpenDayMore(null);

        // Fetch available slots
        const today = new Date();
        const currentWeekMonday = getCurrentWeekMonday(today);
        const startDate = format(addDays(currentWeekMonday, 0), 'yyyy-MM-dd');
        const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

        if (userRole === 'patient') {
            dispatch(getAvailableSlots({
                doctorId: recommendation.doctorId,
                startDate,
                endDate,
                timeZone: systemTimeZone,
                addressType: recommendation.addressType,
                appointmentTypeId: typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType,
                duration: recommendation.duration,
                isReschedulingProcess: true,
                appointmentId: recommendation.appointmentId,
            }));
        } else {
            dispatch(getDoctorAvailableSlots({
                doctorId: recommendation.doctorId,
                startDate,
                endDate,
                timeZone: systemTimeZone,
                addressType: recommendation.addressType,
                appointmentTypeId: typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType,
                duration: recommendation.duration,
                isReschedulingProcess: true,
                appointmentId: recommendation.appointmentId,
            }));
        }

        // Scroll to bottom to show available slots
        scrollDetailsToBottom();
    };

    // Handle direct slot click for rescheduling in medical history
    const handleDirectSlotClickInHistory = async (slot, recommendation) => {
        const slotDateTime = new Date(slot.start);

        // Check if the selected slot is in the past
        if (slotDateTime <= new Date()) {
            toast.warn(t('medical_history.slot_passed_text'));
            return;
        }

        // Shown as "Sep 16, 2026 2:30 PM"; the payload below keeps "HH:mm".
        const shownWhen = `${format(slotDateTime, 'MMM d, yyyy')} ${formatTime(slotDateTime)}`;
        const result = await Swal.fire({
            title: t('medical_history.confirm_reschedule_title', { defaultValue: t('patientappointment.confirm_reschedule_title') }),
            text: t('medical_history.confirm_reschedule_text', { date: shownWhen, defaultValue: `Are you sure you want to reschedule to ${shownWhen}?` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('medical_history.yes_reschedule_button') || t('patientappointment.yes_reschedule_button'),
            cancelButtonText: t('medical_history.cancel_button') || t('patientappointment.cancel_button')
        });

        if (result.isConfirmed) {
            try {
                const date = format(slotDateTime, 'yyyy-MM-dd');
                const time = format(slotDateTime, 'HH:mm');

                // Calculate end time based on duration from the recommendation (original appointment)
                const selectedApptType = selectedDoctor?.appointmentSpeciality
                    ?.find(spec => spec.name === recommendation.specialtyName)
                    ?.appointmentTypes?.find(type => type.type === recommendation.specialtyTypeName || type.type === recommendation.appointTypeName);

                const duration = recommendation.duration || selectedApptType?.duration || '1:00';
                const durationStr = typeof duration === 'number' ? `${Math.floor(duration / 60)}:${duration % 60}` : String(duration);
                const [hours, minutes] = durationStr.split(':').map(Number);
                const slotEndTime = new Date(slotDateTime.getTime() + (hours * 60 + minutes) * 60 * 1000);
                const slotEndTimeStr = format(slotEndTime, 'HH:mm');

                const patientId = selectedPatient?.patientId || localStorage.getItem('ID');

                const payload = {
                    appointmentId: recommendation.appointmentId,
                    patientId: patientId,
                    doctorId: recommendation.doctorId,
                    newDate: date,
                    newTime: time,
                    newSlotEndTime: slotEndTimeStr,
                    timeZone: systemTimeZone,
                    appointmentTypeId: selectedApptType?.typeId || (typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType),
                    description: recommendation.description || '',
                    doctorDescription: recommendation.doctorDescription || null,
                    appointmentMode: recommendation.appointmentMode || 'onsite',
                    SpecialtyId: selectedApptType?.specialtyId || recommendation.SpecialtyId,
                    addressType: recommendation.addressType,
                    duration: durationStr,
                };

                const actionResult = await dispatch(rescheduleAppointment(payload));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    setShowDetails(false);
                    setCurrentRecommendation(null);
                    setIsViewingMoreSlots(false);
                    setWeekOffsetMore(0);
                    setOpenDayMore(null);

                    toast.success(t('medical_history.reschedule_success_text') || 'Appointment rescheduled successfully!');

                    // Refresh medical history
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: 'Rescheduling',
                        page: 1,
                        limit: 5
                    }));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('medical_history.reschedule_error_text') || 'Failed to reschedule', i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('medical_history.reschedule_error_text') || 'Failed to reschedule appointment', i18n.language));
            }
        }
    };

    // Handle week navigation for view more slots in medical history
    const handleNextWeekMore = () => {
        setWeekOffsetMore(prev => Math.min(2, prev + 1));
        setOpenDayMore(null);
    };

    const handlePrevWeekMore = () => {
        setWeekOffsetMore(prev => Math.max(0, prev - 1));
        setOpenDayMore(null);
    };

    // Toggle day drawer for view more slots in medical history
    const toggleDayDrawerMore = (day) => {
        setOpenDayMore(openDayMore === day ? null : day);
    };

    // Fetch slots when week navigation changes (prev/next week buttons)
    useEffect(() => {
        if (isViewingMoreSlots && currentRecommendation) {
            const today = new Date();
            const currentWeekMonday = getCurrentWeekMonday(today);
            const startDate = format(addDays(currentWeekMonday, weekOffsetMore * 7), 'yyyy-MM-dd');
            const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

            if (userRole === 'patient') {
                dispatch(getAvailableSlots({
                    doctorId: currentRecommendation.doctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: currentRecommendation.addressType,
                    appointmentTypeId: typeof currentRecommendation.appointmentType === 'object' ? currentRecommendation.appointmentType?.typeId : currentRecommendation.appointmentType,
                    duration: currentRecommendation.duration,
                    isReschedulingProcess: true,
                    appointmentId: currentRecommendation.appointmentId,
                }));
            } else {
                dispatch(getDoctorAvailableSlots({
                    doctorId: currentRecommendation.doctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: currentRecommendation.addressType,
                    appointmentTypeId: typeof currentRecommendation.appointmentType === 'object' ? currentRecommendation.appointmentType?.typeId : currentRecommendation.appointmentType,
                    duration: currentRecommendation.duration,
                    isReschedulingProcess: true,
                    appointmentId: currentRecommendation.appointmentId,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekOffsetMore]);

    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    const handleAudioChange = (e) => {
        if (isMediaDisabled()) return;
        const file = e.target.files[0];

        if (!file) {
            setAudioError('');
            return;
        }

        // Validate file type
        if (!file.type.startsWith("audio/")) {
            setAudioError(t("medical_history.invalid_audio_alert"));
            return;
        }

        // Validate file size
        if (file.size > MAX_AUDIO_SIZE) {
            setAudioError(t('medicalHistory.audio_size_exceed'));
            return;
        }

        setAudioError('');
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
    };

    // Doctor flow: collect payment and complete the appointment. Payment is
    // mandatory to complete — there is no "pay later".
    const openCompleteInvoice = () => {
        const docId = selectedAppointment?.doctorId;
        if (docId) dispatch(getPaymentOptions(docId));
        setPaymentModalMode('complete');
        // Start from the method picked at booking; if the server does not offer
        // it, the invoice falls to the first method it does offer.
        setPaymentMethod(selectedAppointment?.payment?.method || 'cash');
        setShowPaymentModal(true);
    };

    // Completing while something is still only a draft (owner, 24 Sep 2026):
    // a note box whose text differs from its saved note, or prescription lines
    // not yet saved. Ask first; "Save & complete" sends EVERY pending section
    // in ONE request (POST /appointment-drafts/:id/commit — all written or
    // none), one "Saved" toast, and opens the invoice only when it landed. A
    // refusal is the server's sentence in one toast; the visit stays open.
    // Already-saved boxes are never re-sent. The per-section Saves are as
    // they were.
    const prescriptionDraftRef = useRef(null);
    // The Check readings block's hand-off (check sets): the same five verbs
    // as the prescription's, plus showRefusal / checkRequired / showMissing
    // for the completion gate.
    const checksDraftRef = useRef(null);
    const draftNoteSections = () => {
        const original = originalNotesRef.current || {};
        return Object.keys(notes).filter((section) => notes[section] !== (original[section] || '') && (notes[section] || '').trim());
    };
    // ONE commit of everything pending on the open visit — the note boxes that
    // differ from their saved note and the prescription's pending lines — in a
    // single POST /appointment-drafts/:id/commit (all written or none), one
    // "Saved" toast. Used by "Save & complete" and by the unsaved-changes
    // dialog's "Save and continue". Returns:
    //   'nothing'    nothing was pending, no request;
    //   'blocked'    a local check failed (inline error + one toast), no request;
    //   'refused'    the server refused (its sentence in one toast);
    //   'committed'  written; the drafts are gone server-side.
    const commitVisitDrafts = async ({ blockedMessage }) => {
        const noteSections = draftNoteSections();
        const rx = prescriptionDraftRef.current;
        const rxPending = !!rx?.hasPendingDraft?.();
        const ck = checksDraftRef.current;
        const ckPending = !!ck?.hasPendingDraft?.();
        if (noteSections.length === 0 && !rxPending && !ckPending) return 'nothing';
        const blocked = () => { toast.warn(blockedMessage); return 'blocked'; };

        // Same local checks as each section's own Save, before any request.
        const noteErrors = {};
        noteSections.forEach((section) => {
            const error = validateNotesInput(notes[section] ?? '', t);
            if (error) noteErrors[section] = error;
        });
        if (Object.keys(noteErrors).length > 0) {
            setNotesErrors((prev) => ({ ...prev, ...noteErrors }));
            return blocked();
        }
        const rxPayload = rxPending ? rx.pendingPayload?.() : null;
        if (rxPending && !rxPayload) return blocked();
        // Check readings: `{ [setId]: { fieldKey: value } }`, or null when a
        // set fails its own checks (the block shows the fields).
        const ckPayload = ckPending ? ck.pendingPayload?.() : null;
        if (ckPending && !ckPayload) return blocked();

        const body = {};
        if (noteSections.length > 0) {
            body.notes = Object.fromEntries(noteSections.map((section) => [section, notes[section]]));
        }
        if (rxPayload && (rxPayload.create.length > 0 || rxPayload.update.length > 0)) {
            body.prescription = { create: rxPayload.create, update: rxPayload.update };
        }
        if (ckPayload && Object.keys(ckPayload).length > 0) body.checks = ckPayload;
        const appointmentId = selectedCard.appointmentId;
        setCommitting(true);
        // No debounced draft write may land after the commit deleted it.
        suspendAutosave(appointmentId);
        try {
            const res = await dispatch(commitDrafts({ appointmentId, body }));
            if (!commitDrafts.fulfilled.match(res)) {
                const refusal = res.payload || {};
                const message = serverReason(refusal, blockedMessage, i18n.language);
                const fieldId = typeof refusal.section === 'string' && refusal.section.startsWith('notes.')
                    ? refusal.section.slice('notes.'.length) : null;
                if (fieldId) setNotesErrors((prev) => ({ ...prev, [fieldId]: message }));
                // A check value the server refused: the block opens that set
                // and puts the sentence under the field it names.
                if (typeof refusal.section === 'string' && refusal.section.startsWith('checks.')) {
                    checksDraftRef.current?.showRefusal?.({ section: refusal.section, field: refusal.field || null, message });
                }
                toast.warn(message);
                return 'refused';
            }
            toast.success(t('appointmentDraft.committed', 'Saved'));
            setNotesErrors((prev) => {
                const next = { ...prev };
                noteSections.forEach((section) => { next[section] = ''; });
                return next;
            });
            const committedNotes = Object.fromEntries(noteSections.map((section) => [section, notes[section]]));
            originalNotesRef.current = { ...originalNotesRef.current, ...committedNotes };
            // The store's row carries the committed text too, so a re-render
            // before the re-read below lands keeps it as the saved baseline.
            if (noteSections.length > 0) dispatch(applySavedNotes({ appointmentId, notes: committedNotes }));
            // The server deleted the drafts it committed: clear their chips
            // without another DELETE.
            noteSections.forEach((section) => noteDrafts.clearLocal(noteDraftSection(section)));
            if (body.prescription || rxPending) prescriptionDraftRef.current?.markCommitted?.();
            if (body.checks || ckPending) checksDraftRef.current?.markCommitted?.();
            dispatch(getAppointmentById(appointmentId));
            return 'committed';
        } finally {
            resumeAutosave(appointmentId);
            setCommitting(false);
        }
    };

    // CHECK SETS: a set the doctor marked "Required in every visit" must have
    // a reading before the visit can be completed — the server refuses the
    // completion (409 REQUIRED_CHECKS_MISSING) otherwise. Asked BEFORE the
    // invoice opens, from the server's own `requiredMissing`, so nobody fills
    // an invoice for a visit that cannot complete. True = something is
    // missing (the dialog was shown and the block points at it).
    const requiredChecksMissing = async () => {
        const ck = checksDraftRef.current;
        if (!ck?.checkRequired) return false;
        const missing = await ck.checkRequired();
        if (!Array.isArray(missing) || missing.length === 0) return false;
        ck.showMissing?.(missing);
        const lines = missing.map((m) => {
            const fields = (m.missingFields || []).map((f) => f.label).filter(Boolean);
            const text = fields.length
                ? t('checkSets.missing_set_fields', { set: m.name, fields: fields.join(', ') })
                : t('checkSets.missing_set_only', { set: m.name });
            return `<li>${escapeHtml(text)}</li>`;
        }).join('');
        await Swal.fire({
            title: t('checkSets.missing_title', 'Readings still needed'),
            html: `<p>${escapeHtml(t('checkSets.missing_text'))}</p><ul style="text-align:left;margin:8px auto 0;display:inline-block;list-style:disc">${lines}</ul>`,
            icon: 'warning',
            confirmButtonText: t('checkSets.ok', 'OK'),
            confirmButtonColor: '#4ABDBD',
        });
        return true;
    };

    const handleCompleteClick = async () => {
        if (committing) return;
        const noteSections = draftNoteSections();
        const rxPending = !!prescriptionDraftRef.current?.hasPendingDraft?.();
        const ckPending = !!checksDraftRef.current?.hasPendingDraft?.();
        if (noteSections.length === 0 && !rxPending && !ckPending) {
            if (await requiredChecksMissing()) return;
            openCompleteInvoice();
            return;
        }
        const result = await Swal.fire({
            title: t('appointmentDraft.complete_title', 'Unsaved notes or prescription'),
            text: t('appointmentDraft.complete_text', 'Some of what you typed is only a draft. Save it and complete the visit?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('appointmentDraft.complete_confirm', 'Save & complete'),
            cancelButtonText: t('appointmentDraft.complete_cancel', 'Cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#9ca3af',
            allowOutsideClick: false,
        });
        if (!result?.isConfirmed) return;
        const outcome = await commitVisitDrafts({
            blockedMessage: t('appointmentDraft.complete_blocked', 'Not everything could be saved, so the visit was not completed.'),
        });
        if (outcome === 'committed' || outcome === 'nothing') {
            if (await requiredChecksMissing()) return;
            openCompleteInvoice();
        }
    };

    // The desk methods the server offers for completing this visit — only
    // meaningful once GET /payment/options has answered.
    const completeDeskMethods = (completePaymentOptions?.methods || [])
        .filter((m) => m && typeof m.key === 'string' && !m.requiresGateway && m.key !== 'paylater');
    const resolveCompleteMethod = () => {
        if (isPaidStatus(selectedAppointment?.payment?.status)) return paymentMethod;
        return completeDeskMethods.some((m) => m.key === paymentMethod)
            ? paymentMethod
            : completeDeskMethods[0]?.key || null;
    };

    // Patient flow: settle the appointment themselves at any time before it's
    // completed — only by a method the server offers them (patientPayMethods).
    const patientMethods = patientPayMethods(completePaymentOptions);
    const resolvePayMethod = () => (patientMethods.some((m) => m.key === paymentMethod)
        ? paymentMethod
        : patientMethods[0]?.key || null);

    const handlePayClick = () => {
        const docId = selectedAppointment?.doctorId;
        if (docId) dispatch(getPaymentOptions(docId));
        setPaymentModalMode('pay');
        // Start from the method picked at booking; if it is not one the patient
        // may pay with, the invoice falls to the first one they may.
        setPaymentMethod(selectedAppointment?.payment?.method || '');
        setShowPaymentModal(true);
    };

    // The invoice modal IS the confirmation now (no extra popup). In 'complete'
    // mode the doctor records payment + completes; in 'pay' mode the patient just
    // settles the payment (the appointment stays Booked until the doctor completes).
    // Re-read the open tab of THIS patient's own history.
    const refreshOwnHistory = () => {
        const patientId = selectedPatient?.patientId || (userRole === 'patient' ? localStorage.getItem('ID') : null);
        if (!patientId) return;
        isFetchingRef.current = true;
        dispatch(getMedicalHistory({ patientId, status: selectedTab, page: 1, limit: 5 }))
            .finally(() => { isFetchingRef.current = false; });
    };

    const handlePaymentSubmit = () => {
        const appointmentId = selectedCard.appointmentId || '';
        const refreshList = refreshOwnHistory;

        setSavingField('payment');

        // Patient early-payment: record the payment, do NOT complete the appointment.
        if (paymentModalMode === 'pay') {
            const payMethod = resolvePayMethod();
            if (!payMethod) {
                setSavingField(null);
                return;
            }
            dispatch(payAppointment({ appointmentId, method: payMethod }))
                .unwrap()
                .then(() => {
                    setShowPaymentModal(false);
                    toast.success(t('medical_history.payment_recorded_text', 'Payment recorded successfully!'));
                    // Refresh the open appointment so its "Paid" badge appears.
                    if (appointmentId) dispatch(getAppointmentById(appointmentId));
                    refreshList();
                })
                .catch((error) => {
                    // The payload is an object ({ message, status, data } from the
                    // slice, or the API's { en, fr }) — unwrap it to a string or
                    // React refuses to render it as a toast child.
                    toast.warn(extractErrorMessage(error, t('medical_history.payment_error_text'), i18n.language));
                })
                .finally(() => {
                    setSavingField(null);
                });
            return;
        }

        // Doctor completion: payment must be collected to complete (Paid, always),
        // and only by a method the server offers for this doctor.
        const completeMethod = resolveCompleteMethod();
        if (!completeMethod) {
            setSavingField(null);
            return;
        }
        dispatch(updateAppointment({
            appointmentId,
            method: completeMethod,
            paymentStatus: 'Paid',
            date: new Date().toISOString(),
            appointmentStatus: 'Completed',
        }))
            .unwrap()
            .then(() => {
                setShowPaymentModal(false);
                setShowDetails(false);
                toast.success(t('medical_history.payment_success_regular_text'));
                // Refresh the appointment list to drop the completed appointment from Booked.
                if (selectedTab === 'Booked') refreshList();
            })
            .catch((error) => {
                // A required check set still has no reading (the server's
                // completion gate): close the invoice and point at the sets.
                if (error?.data?.code === 'REQUIRED_CHECKS_MISSING') {
                    setShowPaymentModal(false);
                    checksDraftRef.current?.showMissing?.(error.data.sets || []);
                }
                // Same as above: the 422 "payment must be collected before
                // completing" and the 409 slot conflicts arrive as objects.
                toast.warn(extractErrorMessage(error, t('medical_history.payment_error_text'), i18n.language));
            })
            .finally(() => {
                setSavingField(null);
            });
    };

    const handleImageChange = (e) => {
        if (isMediaDisabled()) return;
        const file = e.target.files[0];

        if (!file) {
            setImageError('');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setImageError(t('medical_history.invalid_image_alert'));
            return;
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
            setImageError(t('medicalHistory.image_size_exceed'));
            return;
        }

        setImageError('');
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleCardClick = async (item) => {
        // Check for unsaved changes before switching
        if (hasUnsavedChanges() && selectedCard?.appointmentId && selectedCard.appointmentId !== item.appointmentId) {
            await confirmUnsavedChanges(() => {
                proceedCardClick(item);
            });
            return;
        }
        proceedCardClick(item);
    };

    const proceedCardClick = async (item) => {
        console.log('item', item);
        const { date, time } = formatDateTime(item.localTime);
        setSelectedCard({
            speciality: item.specialtyName,
            // Carry the specialtyId alongside the snapshot name so the detail panel
            // header can resolve the translated label via <MasterLabel>.
            specialtyId: item.SpecialtyId,
            type: item.specialtyTypeName,
            color: item.colour,
            date,
            time,
            appointmentId: item.appointmentId
        });
        setShowDetails(true);
        setViewingImageUpload(null);

        // A doctor opening a COLLEAGUE's visit: the server refuses the read
        // (403 NOT_YOUR_APPOINTMENT, R3), so it is not asked — the panel says
        // whose visit it is instead of showing an error or a stale appointment.
        if (isColleagueVisit(item, userRole, myId)) {
            setIsCardLoading(false);
            setCurrentRecommendation(null);
            setVisitNotice({ kind: 'colleague' });
            return;
        }
        setVisitNotice(null);
        setIsCardLoading(true);

        // Store recommendation data if Rescheduling tab (for all roles to view)
        if (selectedTab === 'Rescheduling') {
            setCurrentRecommendation(item);
        }

        try {
            const promises = [dispatch(getAppointmentById(item.appointmentId)), new Promise(resolve => setTimeout(resolve, 1000))];
            // Fetch doctor settings for patient role in Booked tab to determine Connect Meet button visibility
            if (userRole === 'patient' && selectedTab === 'Booked' && item.doctorId) {
                promises.push(dispatch(getDoctorSettings(item.doctorId)));
            }
            const result = await Promise.all(promises);
            setIsCardLoading(false);

            // The read was refused: say why, in the server's words, rather than
            // leaving the previous card's appointment on screen.
            if (result[0]?.meta?.requestStatus === 'rejected') {
                const refusal = result[0].payload;
                const body = refusal?.response?.data || refusal?.data || {};
                if (body.code === 'NOT_YOUR_APPOINTMENT') {
                    setVisitNotice({ kind: 'colleague' });
                } else {
                    setVisitNotice({
                        kind: 'refused',
                        message: serverReason(refusal, t('medical_history.visit_refused', 'This visit could not be opened.'), i18n.language),
                    });
                }
                setCurrentRecommendation(null);
                return;
            }

            // If Rescheduling tab, merge appointment data with recommendation data
            if (selectedTab === 'Rescheduling' && result[0]?.payload) {
                setCurrentRecommendation(prev => ({
                    ...prev,
                    ...result[0].payload,
                    ...item // Keep original item data
                }));
            }
        } catch (error) {
            setIsCardLoading(false);
            toast.warn(t('medical_history.fetch_error_message'));
        }
    };

    const handleBackClick = async () => {
        if (hasUnsavedChanges()) {
            await confirmUnsavedChanges(() => {
                setShowDetails(false);
                setCurrentRecommendation(null);
                setVisitNotice(null);
            });
            return;
        }
        setShowDetails(false);
        setCurrentRecommendation(null);
        setVisitNotice(null);
    };

    // Save and restore scroll position for right panel
    const saveScrollPosition = () => {
        if (detailsContainerRef.current) {
            scrollPositionRef.current = detailsContainerRef.current.scrollTop;
            shouldRestoreScrollRef.current = true;
        }
    };
    const restoreScrollPosition = () => {
        shouldRestoreScrollRef.current = true;
    };

    // Track scroll position continuously so we always have the latest
    const handleDetailsScroll = () => {
        if (detailsContainerRef.current && !shouldRestoreScrollRef.current) {
            scrollPositionRef.current = detailsContainerRef.current.scrollTop;
        }
    };

    // Restore scroll using MutationObserver to detect when DOM updates complete
    useEffect(() => {
        const container = detailsContainerRef.current;
        if (!container) return;

        const observer = new MutationObserver(() => {
            if (shouldRestoreScrollRef.current && scrollPositionRef.current > 0) {
                container.scrollTop = scrollPositionRef.current;
            }
        });

        observer.observe(container, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [showDetails, selectedAppointment]);

    // Also restore on every render as fallback
    useEffect(() => {
        if (shouldRestoreScrollRef.current && detailsContainerRef.current && scrollPositionRef.current > 0) {
            requestAnimationFrame(() => {
                if (detailsContainerRef.current) {
                    detailsContainerRef.current.scrollTop = scrollPositionRef.current;
                    // Keep the flag true for a short time to handle multiple re-renders
                    setTimeout(() => {
                        shouldRestoreScrollRef.current = false;
                    }, 500);
                }
            });
        }
    });

    // Resolves true only when the note is on the server (the complete-visit
    // prompt stops on anything else).
    const handleSaveNotes = async (section) => {
        if (isInputDisabled()) return false;
        saveScrollPosition();
        const appointmentId = selectedCard.appointmentId;
        const noteContent = notes[section] ?? '';

        // Validate notes content
        const error = validateNotesInput(noteContent, t);
        if (error) {
            setNotesErrors((prev) => ({
                ...prev,
                [section]: error
            }));
            return false;
        }

        if (!noteContent.trim()) {
            // Inline, next to the box — not a native browser alert().
            setNotesErrors((prev) => ({
                ...prev,
                [section]: t('medical_history.empty_notes_error', 'Please enter some notes before saving.'),
            }));
            return false;
        }

        // Clear error if valid
        setNotesErrors((prev) => ({
            ...prev,
            [section]: ''
        }));

        setSavingField(`notes:${section}`);
        try {
            const result = await dispatch(updateAppointmentCustomNote({
                appointmentId,
                fieldId: section,
                value: noteContent,
            }));

            if (result.meta.requestStatus === 'fulfilled') {
                toast.success(t('medical_history.save_success_message'));
                originalNotesRef.current = { ...originalNotesRef.current, [section]: noteContent };
                dispatch(applySavedNotes({ appointmentId, notes: { [section]: noteContent } }));
                // Committed: the draft has done its job.
                noteDrafts.committed(noteDraftSection(section));
                return true;
            }
            toast.warn(serverReason(result.payload, t('medical_history.save_error_message'), i18n.language));
            return false;
        } catch (error) {
            toast.warn(t('medical_history.save_error_message'));
            return false;
        } finally {
            setSavingField(null);
            restoreScrollPosition();
        }
    };

    const handleSaveAudio = async () => {
        if (isMediaDisabled()) return;
        saveScrollPosition();
        setSavingField('audio');
        try {
            const result = await dispatch(
                uploadAudio({
                    appointmentId: selectedCard.appointmentId,
                    audio: audioFile,
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                toast.success(t("medical_history.audio_upload_success_message"));
                setAudioFile(null);
                setAudioPreview(null);
            } else {
                toast.warn(serverReason(result.payload, t("medical_history.audio_upload_error_message"), i18n.language));
            }
        } catch (error) {
            toast.warn(serverReason(error, t("medical_history.audio_upload_error_message"), i18n.language));
        } finally {
            setSavingField(null);
            restoreScrollPosition();
        }
    };

    const handleSaveImage = async () => {
        if (isMediaDisabled()) return;
        saveScrollPosition();
        if (!imageFile) {
            setImageError(t('medical_history.no_image_selected'));
            return;
        }
        const currentUploads = selectedAppointment?.imageUploads || [];
        if (currentUploads.length >= 5) {
            toast.warn(t('medical_history.max_images_text', 'Maximum 5 images allowed per appointment.'));
            return;
        }
        setSavingField('image');
        try {
            const result = await dispatch(uploadImage({ appointmentId: selectedCard.appointmentId, image: imageFile }));
            if (result.meta.requestStatus === 'fulfilled') {
                toast.success(t('medical_history.image_upload_success_message'));
                setImageFile(null);
                setImagePreview(null);
            } else {
                toast.warn(serverReason(result.payload, t('medical_history.image_upload_error'), i18n.language));
            }
        } catch (error) {
            toast.warn(serverReason(error, t('medical_history.image_upload_error'), i18n.language));
        } finally {
            setSavingField(null);
            restoreScrollPosition();
        }
    };

    const handleDeleteImage = async (uploadId) => {
        if (isMediaDisabled()) return;
        saveScrollPosition();
        const result = await Swal.fire({
            title: t('medical_history.delete_image_title', 'Remove Image'),
            text: t('medical_history.delete_image_text', 'Are you sure you want to remove this image and its OCR notes?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: t('medical_history.delete_confirm', 'Remove'),
            cancelButtonText: t('medical_history.delete_cancel', 'Cancel'),
        });
        if (result.isConfirmed) {
            try {
                await dispatch(deleteUploadedImage({ appointmentId: selectedCard.appointmentId, uploadId })).unwrap();
                toast.success(t('medical_history.image_deleted_text', 'Image removed successfully.'));
            } catch (error) {
                toast.warn(serverReason(error, t('medical_history.image_delete_error', 'Failed to remove image.'), i18n.language));
            }
        }
        restoreScrollPosition();
    };

    const handleDeleteAudioUpload = async (uploadId) => {
        if (!uploadId || isMediaDisabled()) return;
        saveScrollPosition();
        const result = await Swal.fire({
            title: t('medical_history.delete_audio_title', 'Remove audio?'),
            text: t('medical_history.delete_audio_text', 'Are you sure you want to remove this audio clip and its transcript?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: t('medical_history.delete_confirm', 'Remove'),
            cancelButtonText: t('medical_history.delete_cancel', 'Cancel'),
        });
        if (result.isConfirmed) {
            try {
                await dispatch(deleteAudio({ appointmentId: selectedCard.appointmentId, uploadId })).unwrap();
                toast.success(t('medical_history.audio_deleted_text', 'Audio removed successfully.'));
            } catch (error) {
                toast.warn(serverReason(error, t('medical_history.audio_delete_error', 'Failed to remove audio.'), i18n.language));
            }
        }
        restoreScrollPosition();
    };

    const handleCancel = async (data) => {
        try {
            const confirmResult = await Swal.fire({
                title: t('medical_history.cancel_confirm_title'),
                text: t('medical_history.cancel_confirm_text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('medical_history.cancel_confirm_button'),
                cancelButtonText: t('medical_history.cancel_cancel_button')
            });

            if (confirmResult.isConfirmed) {
                // No doctorId: given one, the thunk reloads that doctor's whole
                // diary (GET /appointment/doctor/:id) — other patients' visits.
                // This screen is one patient's history; refresh only that.
                const result = await dispatch(cancelAppointment({ appointmentId: data.appointmentId }));

                if (result.meta.requestStatus === 'fulfilled') {
                    setOpenMenuIndex(null);
                    if (data.patientId) dispatch(getPatientById(data.patientId));
                    refreshOwnHistory();
                    toast.success(t('medical_history.cancel_success_message'));
                } else {
                    toast.warn(serverReason(result.payload, t('medical_history.cancel_error_message'), i18n.language));
                }
            }
        } catch (error) {
            toast.warn(serverReason(error, t('medical_history.cancel_error_message'), i18n.language));
        }
    };

    const handleReschedule = async (data) => {
        if (userRole === 'patient') {
            navigate('/patientAppointment', { state: { reschedulestate: data } });
        }
    };

    // Handle slot selection from recommendation (for Rescheduling tab)
    const handleRecommendationSlotClick = async (slotDateTimeString, recommendation) => {

        const slotDateTime = new Date(slotDateTimeString);

        // Check if the selected slot is in the past
        if (slotDateTime <= new Date()) {
            toast.warn(t('medical_history.slot_passed_text'));
            return;
        }

        const result = await Swal.fire({
            title: t('patientappointment.confirm_reschedule_title'),
            text: t('patientappointment.confirm_reschedule_text', { date: `${format(slotDateTime, 'MMM d, yyyy')} ${formatTime(slotDateTime)}` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('patientappointment.yes_reschedule_button'),
            cancelButtonText: t('patientappointment.cancel_button')
        });

        if (result.isConfirmed) {
            try {
                const selectedApptType = selectedDoctor?.appointmentSpeciality
                    ?.find(spec => spec.name === recommendation.specialtyName)
                    ?.appointmentTypes?.find(type => type.type === recommendation.specialtyTypeName || type.type === recommendation.appointTypeName);

                const date = format(slotDateTime, 'yyyy-MM-dd');
                const time = format(slotDateTime, 'HH:mm');

                // Calculate end time based on duration from the recommendation (original appointment)
                const duration = recommendation.duration || selectedApptType?.duration || '1:00';
                const durationStr = typeof duration === 'number' ? `${Math.floor(duration / 60)}:${duration % 60}` : String(duration);
                const [hours, minutes] = durationStr.split(':').map(Number);
                const slotEndTime = new Date(slotDateTime.getTime() + (hours * 60 + minutes) * 60 * 1000);
                const slotEndTimeStr = format(slotEndTime, 'HH:mm');

                const patientId = selectedPatient?.patientId || localStorage.getItem('ID');

                const payload = {
                    appointmentId: recommendation.appointmentId,
                    patientId: patientId,
                    doctorId: recommendation.doctorId,
                    newDate: date,
                    newTime: time,
                    newSlotEndTime: slotEndTimeStr,
                    timeZone: systemTimeZone,
                    appointmentTypeId: selectedApptType?.typeId || (typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType),
                    description: recommendation.description || '',
                    doctorDescription: recommendation.doctorDescription || null,
                    appointmentMode: recommendation.appointmentMode || 'onsite',
                    SpecialtyId: selectedApptType?.specialtyId || recommendation.SpecialtyId,
                    addressType: recommendation.addressType,
                    duration: durationStr,
                };

                const actionResult = await dispatch(rescheduleAppointment(payload));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    setShowDetails(false);
                    setCurrentRecommendation(null);
                    // Refresh medical history
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: 'Rescheduling',
                        page: 1,
                        limit: 5
                    }));

                    toast.success(t('patientappointment.reschedule_success_text'));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.reschedule_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.reschedule_error_text'), i18n.language));
            }
        }
    };

    // Handle cancel appointment from recommendation (for Rescheduling tab)
    const handleCancelRecommendationAppointment = async (recommendation) => {

        const result = await Swal.fire({
            title: t('patientappointment.cancel_appointment_confirm_title'),
            text: t('patientappointment.cancel_appointment_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('patientappointment.yes_cancel_button'),
            cancelButtonText: t('patientappointment.no_button')
        });

        if (result.isConfirmed) {
            try {
                // No doctorId — see handleCancel: never reload the doctor's diary here.
                const actionResult = await dispatch(cancelAppointment({
                    appointmentId: recommendation.appointmentId,
                }));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    setShowDetails(false);
                    setCurrentRecommendation(null);
                    // Refresh medical history
                    const patientId = selectedPatient?.patientId || localStorage.getItem('ID');
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: 'Rescheduling',
                        page: 1,
                        limit: 5
                    }));

                    toast.success(t('patientappointment.cancel_success_text'));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.cancel_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.cancel_error_text'), i18n.language));
            }
        }
    };

    const tabs = STATUS_TABS;

    const handleNavTab = async (tab) => {
        if (hasUnsavedChanges()) {
            await confirmUnsavedChanges(() => {
                proceedNavTab(tab);
            });
            return;
        }
        proceedNavTab(tab);
    };

    const proceedNavTab = (tab) => {
        setShowDetails(false);
        setVisitNotice(null);
        setIsSwitchingTab(true);
        setSelectedTab(tab);
        setSelectedCard({
            speciality: '',
            type: '',
            color: '#000000',
            date: '',
            time: '',
            appointmentId: ''
        });
        setCurrentRecommendation(null);
    };

    return (
        // In the calendar's in-place visit view the parent is a fixed-height
        // flex column: this root fills it and only the two panes below scroll
        // (no page scroll). Everywhere else the old content-height layout stays.
        <div data-testid="medical-history" className={isOpenAppointmentMode ? 'flex flex-col flex-1 min-h-0 overflow-hidden' : ''}>
            {/* In the in-place visit the root clips (overflow-hidden, so only the
                panes scroll): the active chip's scale-105 and shadow poked past
                the row's edge and were cut at the top and left. The row keeps
                its own overflow visible and pads the chips inside the clip. */}
            <div data-testid="medical-history-tabs-row" className={`flex gap-2 mb-6 flex-wrap items-center ${isOpenAppointmentMode ? 'shrink-0 overflow-visible px-2 pt-2 pb-1' : ''}`}>
                <div data-testid="medical-history-tabs" className="flex gap-2 flex-wrap flex-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            data-testid={`medical-history-tab-${tab.toLowerCase()}`}
                            className={`relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 w-fit text-center shadow-md hover:shadow-lg ${selectedTab === tab
                                ? 'text-white scale-105'
                                : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 hover:from-gray-300 hover:to-gray-400 hover:scale-105 cursor-pointer'
                                }`}
                            onClick={() => handleNavTab(tab)}
                        >
                            {selectedTab === tab && (
                                <motion.div
                                    layoutId="activeMedicalHistoryStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-md !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{t(`medical_history.tabs.${tab.toLowerCase()}`)}</span>
                        </button>
                    ))}
                </div>

                {/* Right side buttons for Summary and Ask AI. The overall summary
                    is a doctor's digest of THEIR OWN completed visits with this
                    patient — POST /appointment/patient-overall-summary answers
                    anyone else 403 OVERALL_SUMMARY_DOCTOR_ONLY (R1), an admin
                    included, so only a doctor is offered it — and since 1 Oct
                    2026 only a doctor whose plan carries ai_appointment_summary
                    (403 PLAN_FEATURE_MISSING otherwise). */}
                {userRole === 'doctor' && summaryPlanOn && !isOpenAppointmentMode && (
                    <div data-testid="medical-history-side-actions" className="flex gap-4 ml-auto">
                        <button
                            data-testid="medical-history-summary-button"
                            onClick={() => setIsSummaryDrawerOpen(true)}
                            className="bg-[#e2e6eb] text-[#2c3e50] font-semibold flex items-center gap-2 px-5 py-2.5 rounded-2xl shadow hover:bg-[#d1d8e0] transition-colors cursor-pointer"
                        >
                            <FileText size={18} />
                            {t('medicalHistory.summary')}
                        </button>
                        {/* <button
                            onClick={() => setIsAskAIDrawerOpen(true)}
                            className="bg-[#3A9B9B] text-white font-semibold flex items-center gap-2 px-5 py-2.5 rounded-2xl shadow hover:bg-[#2e7d7d] transition-colors cursor-pointer"
                        >
                            <MessageSquare size={18} />
                            {t('medicalHistory.ask_ai')}
                        </button> */}
                    </div>
                )}
            </div>

            {medicalHistoryLoading && (!medicalHistoryData || medicalHistoryData.length === 0) ? (
                <div data-testid="medical-history-initial-loading" className="flex flex-col items-center justify-center min-h-[50vh]">
                    <CommonLoader />
                    <span data-testid="medical-history-initial-loading-text" className="text-lg sm:text-xl text-[#4ABDBD] mt-4">{t('patientpage.loading')}</span>
                </div>
            ) : (!medicalHistoryData || medicalHistoryData.length === 0) ? (
                <div data-testid="medical-history-empty" className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                    <img
                        data-testid="medical-history-empty-image"
                        src={emptymedicalhistory}
                        alt={t('medical_history.no_data_alt')}
                        className="w-2/3 sm:w-1/2 md:w-1/3 lg:w-1/4 max-w-[200px] mb-4"
                    />
                    <p data-testid="medical-history-empty-message" className="text-lg font-semibold text-gray-600">
                        {t('medical_history.no_data_message')}
                    </p>
                    {userRole === 'patient' && (
                        <>
                            <p data-testid="medical-history-empty-submessage" className="text-sm text-gray-500 mt-2">
                                {t('medical_history.no_data_submessage')}
                            </p>
                            <button
                                data-testid="medical-history-book-appointment-button"
                                onClick={() => navigate("/patientAppointment")}
                                className="mt-4 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            >
                                {t('medical_history.go_to_appointment')}
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div data-testid="medical-history-panes" className={`flex flex-col lg:flex-row gap-4 ${isOpenAppointmentMode ? 'flex-1 min-h-0' : ''}`}>
                    {/* Cards Section */}
                    <div
                        ref={cardsContainerRef}
                        data-testid="medical-history-list-pane"
                        className={`relative w-full lg:w-80 flex-shrink-0 overflow-y-auto custom-scrollbargray pr-4 lg:pr-6 border-r border-gray-200 ${isOpenAppointmentMode ? 'h-full min-h-0' : 'max-h-[calc(100vh-200px)]'} ${showDetails ? 'hidden lg:block' : 'block'
                            }`}
                    >
                        {/* Loading overlay when switching tabs (not for pagination) */}
                        {isSwitchingTab && medicalHistoryLoading && (
                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center rounded-xl">
                                <CommonLoader />
                                <span className="text-sm sm:text-base text-[#4ABDBD] mt-3 font-semibold">{t('patientpage.loading')}</span>
                            </div>
                        )}

                        {medicalHistoryData && medicalHistoryData.map((item, index) => {
                            const { date, time } = formatDateTime(item.localTime);
                            // A colleague's visit is theirs to cancel, not this doctor's.
                            const colleagueVisit = isColleagueVisit(item, userRole, myId);
                            const showMenu = (userRole === 'patient' || userRole === 'doctor') && selectedTab === 'Booked' && !colleagueVisit;
                            const isSelected = selectedCard.appointmentId === item.appointmentId;
                            return (
                                <div
                                    key={item._id || index}
                                    data-testid={`medical-history-card-${item.appointmentId || item._id || index}`}
                                    data-selected={isSelected ? 'true' : 'false'}
                                    className={`group relative flex flex-col p-5 pl-6 bg-gray-50 border border-gray-100 m-1 rounded-2xl shadow-sm mb-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${isSelected ? 'shadow-lg ring-1 ring-black/5' : ''}`}
                                    style={{ borderLeftWidth: '5px', borderLeftColor: item.colour || '#4ABDBD' }}
                                    onClick={(e) => {
                                        e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                        handleCardClick(item);
                                    }}
                                >
                                    {showMenu && (
                                        <button
                                            data-testid={`medical-history-card-menu-${item.appointmentId || item._id || index}`}
                                            className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-[#3A9B9B] hover:bg-[#4ABDBD]/10 transition-colors focus:outline-none cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleMenu(index);
                                            }}
                                        >
                                            <MoreVertical size={18} />
                                        </button>
                                    )}
                                    {openMenuIndex === index && showMenu && (
                                        <div
                                            ref={menuRef}
                                            className="absolute top-11 right-3 w-32 bg-white border border-gray-200 shadow-xl z-10 rounded-xl overflow-hidden"
                                        >
                                            {userRole === 'patient' && (
                                                <button
                                                    data-testid={`medical-history-reschedule-button-${item.appointmentId || index}`}
                                                    className="block w-full text-left px-4 py-2 text-sm bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 font-semibold cursor-pointer"
                                                    onClick={() => handleReschedule(item)}
                                                >
                                                    {t('medical_history.reschedule_button')}
                                                </button>
                                            )}
                                            <button
                                                data-testid={`medical-history-cancel-button-${item.appointmentId || index}`}
                                                className="block w-full text-left px-4 py-2 text-sm text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-300 font-semibold cursor-pointer"
                                                onClick={() => handleCancel(item)}
                                            >
                                                {t('medical_history.cancel_button')}
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 pr-8">
                                        <CalendarCheck size={16} className="text-[#4ABDBD] shrink-0" />
                                        <p className="text-sm font-semibold text-slate-700 whitespace-nowrap tracking-tight">{date}</p>
                                        <span className="text-slate-300">|</span>
                                        <p className="text-sm font-medium text-slate-500 whitespace-nowrap tracking-tight">{time}</p>
                                    </div>
                                    <h3
                                        // `truncate` cannot work against `max-w-fit`: the chip
                                        // grows to fit its text, so there is never an overflow to
                                        // clip and a long specialty name ("Vascular & Endovascular
                                        // Surgery") spills outside the card. Bound it to the card
                                        // width and let the flex item shrink so truncation applies.
                                        // `text-overflow: ellipsis` only clips the box that owns
                                        // the text: on an inline-flex chip the text sits in a
                                        // child span, so the "…" goes on THAT span, and the chip
                                        // itself only bounds the width (owner, 24 Sep 2026).
                                        className="mt-3 inline-flex max-w-full min-w-0 items-center self-start overflow-hidden rounded-full px-3 py-1 text-sm font-semibold text-white shadow-sm shadow-black/5 sm:text-base"
                                        style={{ backgroundColor: item.colour || '#4ABDBD' }}
                                        title={item.specialtyName || undefined}
                                    >
                                        <MasterLabel type="masterSpecialty" id={item.SpecialtyId} field="name" fallback={item.specialtyName} className="block min-w-0 truncate" />
                                    </h3>
                                    <p className="mt-2 text-sm font-medium text-slate-500 truncate pr-12">{item.specialtyTypeName}</p>
                                    {colleagueVisit && (
                                        <span
                                            data-testid={`medical-history-card-colleague-${item.appointmentId || item._id || index}`}
                                            className="mt-2 inline-flex items-center gap-1.5 self-start rounded-full border border-[#C7D6D5] bg-white px-2.5 py-0.5 text-xs font-semibold text-[#2D8A8A]"
                                        >
                                            <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                                            {t('medical_history.colleague_visit', 'Another doctor’s visit')}
                                        </span>
                                    )}
                                    {item.appointmentMode === 'visio' && (
                                        <Video
                                            size={30}
                                            strokeWidth={1.5}
                                            fill="#3A9B9B"
                                            color="#3A9B9B"
                                            className="absolute bottom-3 right-3 drop-shadow-sm"
                                        />
                                    )}
                                    {isSelected && (
                                        <div className="absolute -right-4.5 top-1/2 transform -translate-y-1/2 bg-gray-50 border-r border-t rotate-45 rounded-tr-md shadow-[1px_0px_0px_-0.5px_rgba(0,0,0,0.1)] border-gray-200 p-1" >
                                            <ChevronRight className="text-lg -rotate-45 text-[#3A9B9B]" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Infinite scroll trigger and loading indicator */}
                        {medicalHistoryPage < medicalHistoryTotalPages && (
                            <div ref={scrollObserverRef} className="flex justify-center items-center py-4">
                                {medicalHistoryLoading && (
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="h-6 w-6 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-xs text-gray-500">{t('medical_history.loading_more')}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* End of list indicator */}
                        {medicalHistoryPage >= medicalHistoryTotalPages && medicalHistoryData && medicalHistoryData.length > 0 && (
                            <div className="flex justify-center items-center py-4">
                                <p className="text-xs text-gray-400">{t('medical_history.end_of_list')}</p>
                            </div>
                        )}
                    </div>

                    {/* Details Section */}
                    <div
                        ref={detailsContainerRef}
                        onScroll={handleDetailsScroll}
                        data-testid="medical-history-detail-pane"
                        className={`flex-1 overflow-y-auto custom-scrollbargray ${isOpenAppointmentMode ? 'h-full min-h-0' : 'max-h-[calc(100vh-200px)]'} px-0 sm:px-4 lg:px-6 py-4 ${showDetails ? 'block' : 'hidden lg:block'
                            }`}
                    >
                        {/* Image Detail View */}
                        {viewingImageUploadLive ? (
                            <div className="flex flex-col gap-4">
                                <button
                                    type="button"
                                    data-testid="patient-medical-history-image-back-button"
                                    onClick={() => {
                                        setViewingImageUpload(null);
                                        setTimeout(() => {
                                            if (detailsContainerRef.current) {
                                                detailsContainerRef.current.scrollTop = scrollPositionRef.current;
                                            }
                                        }, 0);
                                    }}
                                    className="flex items-center gap-2 text-sm font-semibold text-[#4ABDBD] hover:text-[#3AA8A8] transition-colors duration-200 cursor-pointer w-fit"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                    {t('medical_history.back_to_notes', 'Back')}
                                </button>
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                    <img
                                        src={getFileURL(viewingImageUploadLive.imageUrl)}
                                        alt={t('medical_history.uploaded_image_alt')}
                                        className="w-full h-auto"
                                    />
                                </div>
                                {(viewingImageUploadLive.ocrNotes || viewingImageUploadLive.status === 'processing') && (
                                    <div>
                                        <p className="text-xs font-semibold text-[#4ABDBD] mb-1">
                                            {t('medical_history.ocr_notes_label')}
                                        </p>
                                        {viewingImageUploadLive.status === 'processing' ? (
                                            mediaTextBlock(viewingImageUploadLive.ocrNotes, 'processing', t('medical_history.generating_ocr', 'Generating OCR notes…'))
                                        ) : (
                                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                {viewingImageUploadLive.ocrNotes}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : showDetails && visitNotice ? (
                            <div data-testid="medical-history-visit-notice" className="flex flex-col">
                                <button
                                    data-testid="medical-history-visit-notice-back-button"
                                    className="lg:hidden mb-4 text-[#4ABDBD] flex items-center gap-2 cursor-pointer w-fit"
                                    onClick={handleBackClick}
                                >
                                    <ArrowLeft size={18} />
                                    <span className="text-sm sm:text-base font-semibold">
                                        {t('medical_history.back_button')}
                                    </span>
                                </button>
                                <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
                                    <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A]">
                                        {visitNotice.kind === 'colleague'
                                            ? <UserRound className="h-7 w-7" aria-hidden="true" />
                                            : <ShieldAlert className="h-7 w-7" aria-hidden="true" />}
                                    </span>
                                    {selectedCard.date && (
                                        <p className="text-xs font-medium text-gray-400 mb-1">{selectedCard.date} · {selectedCard.time}</p>
                                    )}
                                    <h2 data-testid="medical-history-visit-notice-title" className="text-base sm:text-lg font-semibold text-gray-700">
                                        {visitNotice.kind === 'colleague'
                                            ? t('medical_history.colleague_visit', 'Another doctor’s visit')
                                            : t('medical_history.visit_refused_title', 'You can’t open this visit')}
                                    </h2>
                                    <p data-testid="medical-history-visit-notice-message" className="mt-2 max-w-md text-sm text-gray-500">
                                        {visitNotice.kind === 'colleague'
                                            ? t('medical_history.colleague_visit_message', 'Only the doctor who saw the patient on this visit can open its details.')
                                            : visitNotice.message}
                                    </p>
                                </div>
                            </div>
                        ) : showDetails && isCardLoading ? (
                            <div className="flex flex-col items-center justify-center min-h-[50vh]">
                                <CommonLoader />
                                <span className="text-lg sm:text-xl text-[#4ABDBD]">{t('patientpage.loading')}</span>
                            </div>
                        ) : showDetails && selectedAppointment ? (
                            <>
                                <button
                                    data-testid="patient-medical-history-back-mobile-button"
                                    className="lg:hidden mb-4 text-[#4ABDBD] flex items-center gap-2 cursor-pointer"
                                    onClick={handleBackClick}
                                >
                                    <ArrowLeft size={18} />
                                    <span className="text-sm sm:text-base font-semibold">
                                        {t('medical_history.back_button')}
                                    </span>
                                </button>
                                <div className="text-center mb-4 sm:mb-6 flex flex-col items-center">
                                    <span
                                        className="inline-block px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-base sm:text-lg md:text-xl font-bold text-white"
                                        style={{ backgroundColor: selectedCard.color }}
                                    >
                                        <MasterLabel type="masterSpecialty" id={selectedCard.specialtyId} field="name" fallback={selectedCard.speciality} />
                                    </span>
                                    <p className="text-sm sm:text-base font-semibold mt-2" style={{ color: selectedCard.color }}>
                                        {selectedCard.type}
                                    </p>
                                    {/* A programme visit: "<programme> · Stop n" and an
                                        emergency's reason (doc/programme-visits.md §3). */}
                                    <AppointmentProgrammeTag programme={selectedAppointment.programme} className="mt-2" testId="visit-programme-tag" />
                                    {/* The whole health record, opened OVER the visit so the
                                        notes being typed below stay exactly where they are. */}
                                    {ownsVisit && healthPatientId && (
                                        <button
                                            type="button"
                                            data-testid="visit-health-record-button"
                                            onClick={() => setIsHealthDrawerOpen(true)}
                                            className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#4ABDBD]/40 bg-white px-4 py-2 text-sm font-semibold text-[#2D8A8A] shadow-sm transition-colors hover:bg-[#4ABDBD]/10 cursor-pointer"
                                        >
                                            <HeartPulse className="h-4 w-4" aria-hidden="true" />
                                            {t('healthRecord.drawer.open_button', 'Patient health record')}
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
                                    {[
                                        { label: t('medical_history.date_label'), value: selectedCard.date },
                                        { label: t('medical_history.time_label'), value: selectedCard.time },
                                        {
                                            label: t('medical_history.status_label'),
                                            value: selectedAppointment.status && typeof selectedAppointment.status === 'string'
                                                ? (t(`medical_history.tabs.${selectedAppointment.status.toLowerCase()}`) || t('medical_history.status_booked_default'))
                                                : t('medical_history.status_booked_default'),
                                            className: 'text-green-600'
                                        },
                                        {
                                            label: t('medical_history.price_label'),
                                            value: selectedAppointment.price ? `${getCurrencySymbol(selectedAppointment.currency)}${selectedAppointment.price}` : t('medical_history.na'),
                                            priceType: selectedAppointment.pricetype === 'Type 1' ? t('appointmentdetails.price_type_appointment') :
                                                selectedAppointment.pricetype === 'Type 2' ? t('appointmentdetails.price_type_custom') :
                                                    selectedAppointment.pricetype === 'Type 3' ? t('appointmentdetails.price_type_manual') : null
                                        },
                                        {
                                            label: t('medical_history.mode_label'),
                                            value: selectedAppointment.appointmentMode === 'onsite' ? t(`appointmentdetails.Onsite`) : t('medicalHistory.visio')
                                        }
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <p className="text-sm sm:text-base font-semibold text-gray-500">{item.label}:</p>
                                            <p className={`text-sm sm:text-base font-semibold text-gray-400 ${item.className || ''}`}>
                                                {item.value}
                                            </p>
                                            {item.priceType && (userRole === 'doctor' || userRole === 'admin') && (
                                                <p className="text-[10px] sm:text-xs font-medium text-green-600 mt-0.5">
                                                    {item.priceType}
                                                </p>
                                            )}
                                            {item.label === t('medical_history.mode_label') && selectedAppointment.appointmentMode === 'visio' && !selectedAppointment.meetingCompleted && isWithinTwoHoursBeforeAppointment() && selectedTab === 'Booked' && (() => {
                                                // Each role opens ITS OWN link and never the other's.
                                                // The patient used to fall back to doctorMeetingLink when
                                                // patientMeetingLink was missing — but the two links are not
                                                // interchangeable: the doctor's carries an encrypted
                                                // role:'host' token, so that fallback dropped the patient
                                                // into the room named as the doctor, with host powers
                                                // (skip the waiting room, admit strangers, end the call for
                                                // everyone). No link for your role = the video room isn't
                                                // ready; that is a disabled button, not someone else's link.
                                                const meetingLink = userRole === 'doctor'
                                                    ? selectedAppointment.doctorMeetingLink
                                                    : userRole === 'patient'
                                                        ? selectedAppointment.patientMeetingLink
                                                        : null;

                                                return (
                                                    <button
                                                        data-testid="patient-medical-history-connect-meet-button"
                                                        className={`ml-4 px-4 py-2 font-semibold rounded-lg shadow-sm text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 flex items-center gap-2 ${meetingLink
                                                            ? 'bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white hover:from-[#3aa8a8] hover:to-[#2a7b7b] cursor-pointer'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            }`}
                                                        onClick={() => {
                                                            if (!meetingLink) return;
                                                            window.open(meetingLink, '_blank');
                                                        }}
                                                        disabled={!meetingLink}
                                                        title={meetingLink ? undefined : t('medicalHistory.no_meeting_link')}
                                                    >
                                                        {meetingLink
                                                            ? t('medicalHistory.connect_meet')
                                                            : t('medicalHistory.no_meeting_link')}
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>

                                {/* Patient: settle the appointment (online/card) any time before it's
                                    completed; shows a "Paid" badge once settled. Placed in this common
                                    area so it renders for patients regardless of the doctor-only
                                    sections below (which patients don't render). */}
                                {userRole === 'patient' && selectedTab === 'Booked' && selectedAppointment.status === 'Booked' && (
                                    <div className="flex justify-center mb-4 sm:mb-6">
                                        {isPaidStatus(selectedAppointment.payment?.status) ? (
                                            <span data-testid="patient-medical-history-paid-badge" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-green-50 text-green-600 font-semibold text-sm sm:text-base">
                                                <CheckCircle2 className="w-4 h-4" />
                                                {t('payments.paid', 'Paid')}
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid="patient-medical-history-pay-button"
                                                className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm sm:text-base cursor-pointer flex items-center justify-center gap-2"
                                                onClick={handlePayClick}
                                                disabled={patientLoading || appointmentLoading}
                                            >
                                                {(patientLoading || appointmentLoading) ? (
                                                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <>
                                                        <Wallet className="w-4 h-4" />
                                                        {t('medical_history.pay_button', 'Pay')}
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Meeting recording (video + transcript) — shown for ANY status,
                                    so the doctor sees "Generating…" / View as soon as a Visio
                                    meeting is recorded, not only after completion.
                                    A row the server flagged clinicalHidden (a login the
                                    clinical-record rule refuses — staff with appointments.view)
                                    arrives with its notes, transcripts, OCR, summary and media
                                    emptied: say so once, and ask for none of them. */}
                                {/* CHECK READINGS (check sets) — the visit's readings at the
                                    top of the visit, on EVERY tab (owner, 26 Sep 2026: a
                                    completed visit's details showed none): the doctor's own
                                    block — Correct / Add and drafts only while the visit is
                                    open for writing (Booked, or Completed with Edit on),
                                    read-only cards otherwise; the patient's (or a family
                                    login's) read-only view out of the health record; the
                                    desk's view for the admin and staff who may record. Each
                                    hides itself when it has nothing to say. */}
                                {userRole === 'doctor' ? (
                                    <CheckReadingsBlock
                                        appointment={selectedAppointment}
                                        editable={(selectedTab === 'Booked' && selectedAppointment.status !== 'Cancelled')
                                            || (selectedTab === 'Completed' && isEditingCompleted)}
                                        mySwitch={myCheckSwitch}
                                        draftControlRef={checksDraftRef}
                                        className="mb-4 sm:mb-6"
                                    />
                                ) : userRole === 'patient' ? (
                                    <CheckReadingsBlock appointment={selectedAppointment} className="mb-4 sm:mb-6" />
                                ) : userRole === 'user' ? (
                                    <VisitReadingsPanel appointment={selectedAppointment} className="mb-4 sm:mb-6" />
                                ) : null}
                                {/* The hospital ADMIN gets neither block (owner, 28 Sep
                                    2026): readings are taken by the doctor and the desk
                                    staff who hold checks.record — the admin manages the
                                    hospital's sets, not a visit's readings. The API refuses
                                    the admin's record / correct too. */}

                                {/* PROGRAMME — on EVERY status: the doctor gets the card
                                    (the stop line, tick / close) when a programme runs,
                                    else only the small "+ Programme" button; staff with
                                    checks.record the desk strip; the admin nothing (owner,
                                    28 Sep 2026 — the API refuses an admin's start / tick /
                                    close). It hides itself when the hospital has no
                                    programmes or the server says this viewer may not read
                                    them (doc/programme-visits.md). */}
                                {(userRole === 'doctor' || userRole === 'user') && (selectedAppointment.patientId || selectedPatient?.patientId) && (
                                    <ProgrammeCard
                                        patientId={selectedAppointment.patientId || selectedPatient?.patientId}
                                        appointment={selectedAppointment}
                                        className="mb-4 sm:mb-6"
                                    />
                                )}

                                {selectedAppointment.clinicalHidden === true ? (
                                    <ClinicalHiddenNote className="mb-4 sm:mb-6" testId="patient-medical-history-clinical-hidden" />
                                ) : (
                                    <MeetingSummary
                                        appointmentId={selectedAppointment.appointmentId}
                                        expectRecording={/visio/i.test(selectedAppointment?.appointmentMode || '')}
                                        transcriptionOn={aiGates.recordingTranscription}
                                        className="mb-4 sm:mb-6"
                                    />
                                )}

                                {selectedTab === 'Rescheduling' && currentRecommendation ? (
                                    <>
                                        {/* Appointment Details */}
                                        {/* <div className="mb-4 p-4 bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-xl">
                                            <h4 className="text-lg font-semibold text-gray-800 mb-3">{t('patientappointment.appointment_details_heading')}</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('patientappointment.date_time_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.localTime ? `${format(new Date(currentRecommendation.localTime), 'MMM d, yyyy')} ${formatTime(currentRecommendation.localTime)}` : '-'}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('patientappointment.speciality_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.specialtyName || t('medical_history.na')}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('patientappointment.appointment_type_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.specialtyTypeName || currentRecommendation.appointTypeName || t('medical_history.na')}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('medical_history.status_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.status || t('medical_history.na')}</p>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Recommended Slots */}
                                        <div className="mb-4">
                                            <h4 className="text-lg font-semibold text-gray-800 mb-2">{t('patientappointment.select_recommended_slots_heading')}</h4>
                                            <p className="text-sm text-gray-600 mb-4">{t('patientappointment.reschedule_recommendation_message')}</p>
                                            <div className="flex flex-wrap items-center gap-3">
                                                {/* Get recommendation data - merge currentRecommendation and selectedAppointment */}
                                                {(() => {
                                                    const recommendation = { ...currentRecommendation, ...selectedAppointment };

                                                    // Helper function to create slot datetime from date and time or use direct date
                                                    const createSlotDateTime = (dateField, timeField) => {
                                                        if (!dateField) return null;
                                                        // If date_1 is already a full datetime string, use it directly
                                                        if (dateField.includes('T') || dateField.includes(' ')) {
                                                            return new Date(dateField);
                                                        }
                                                        // Otherwise combine date and time
                                                        if (timeField) {
                                                            return new Date(`${dateField}T${timeField}`);
                                                        }
                                                        return new Date(dateField);
                                                    };

                                                    const slot1 = createSlotDateTime(recommendation?.date_1, recommendation?.time_1);
                                                    const slot2 = createSlotDateTime(recommendation?.date_2, recommendation?.time_2);
                                                    const slot3 = createSlotDateTime(recommendation?.date_3, recommendation?.time_3);

                                                    const hasSlots = slot1 || slot2 || slot3;

                                                    if (!hasSlots) {
                                                        return (
                                                            <p className="text-sm text-gray-500 italic">
                                                                {t('medical_history.no_slots_available') || 'No recommended slots available'}
                                                            </p>
                                                        );
                                                    }

                                                    return (
                                                        <>
                                                            {slot1 && (
                                                                <button
                                                                    data-testid={`patient-medical-history-recommendation-slot1-${recommendation.id}`}
                                                                    onClick={() => handleRecommendationSlotClick(slot1.toISOString(), recommendation)}
                                                                    disabled={appointmentLoading}
                                                                    className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                        {format(slot1, 'MMM d, yyyy')} {formatTime(slot1)}
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {slot2 && (
                                                                <button
                                                                    data-testid={`patient-medical-history-recommendation-slot2-${recommendation.id}`}
                                                                    onClick={() => handleRecommendationSlotClick(slot2.toISOString(), recommendation)}
                                                                    disabled={appointmentLoading}
                                                                    className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                        {format(slot2, 'MMM d, yyyy')} {formatTime(slot2)}
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {slot3 && (
                                                                <button
                                                                    data-testid={`patient-medical-history-recommendation-slot3-${recommendation.id}`}
                                                                    onClick={() => handleRecommendationSlotClick(slot3.toISOString(), recommendation)}
                                                                    disabled={appointmentLoading}
                                                                    className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                        {format(slot3, 'MMM d, yyyy')} {formatTime(slot3)}
                                                                    </div>
                                                                </button>
                                                            )}
                                                            <button
                                                                data-testid={`patient-medical-history-view-more-slots-${recommendation.id}`}
                                                                onClick={() => handleViewMoreSlotsInHistory(recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-white border-2 border-gray-400 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-100 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {t('medical_history.view_more_slots_button') || 'View More Slots'}
                                                            </button>
                                                            <button
                                                                data-testid={`patient-medical-history-cancel-recommendation-${recommendation.id}`}
                                                                onClick={() => handleCancelRecommendationAppointment(recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto"
                                                            >
                                                                {appointmentLoading && (
                                                                    <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                                                )}
                                                                {t('patientappointment.cancel_appointment_button')}
                                                            </button>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        {/* View More Slots Section */}
                                        {isViewingMoreSlots && currentRecommendation && (
                                            <div className="mt-6 p-4 sm:p-6 bg-gradient-to-br from-yellow-50/50 to-orange-50/30 border border-yellow-200 rounded-xl">
                                                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                                    {t('medical_history.view_more_slots_title') || 'Select an Available Slot'}
                                                </h4>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    {t('medical_history.view_more_slots_description') || 'Click on any available slot to reschedule your appointment'}
                                                </p>

                                                {appointmentLoading ? (
                                                    <div className="flex justify-center items-center py-8">
                                                        <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-6">
                                                        {/* Week Navigation - Always visible */}
                                                        {(() => {
                                                            const today = new Date();
                                                            const currentWeekMonday = getCurrentWeekMonday(today);
                                                            const startDate = addDays(currentWeekMonday, weekOffsetMore * 7);
                                                            const endDate = addDays(startDate, 6);
                                                            return (
                                                                <div className="flex items-center justify-center gap-4 sm:gap-6 mb-4">
                                                                    <button
                                                                        data-testid="patient-medical-history-prev-week-more-button"
                                                                        onClick={handlePrevWeekMore}
                                                                        disabled={weekOffsetMore === 0}
                                                                        className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                                                                    >
                                                                        <ArrowLeft size={20} />
                                                                    </button>
                                                                    <h3 className="text-lg font-semibold text-gray-800">
                                                                        {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
                                                                    </h3>
                                                                    <button
                                                                        data-testid="patient-medical-history-next-week-more-button"
                                                                        onClick={handleNextWeekMore}
                                                                        disabled={weekOffsetMore === 2}
                                                                        className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                                                                    >
                                                                        <ArrowRight size={20} />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Available Days - use availableSlots for patient, availableDoctorSlots for doctor */}
                                                        {(() => {
                                                            const slotsData = userRole === 'patient' ? availableSlots : availableDoctorSlots;
                                                            if (!slotsData || Object.keys(slotsData).length === 0) {
                                                                return (
                                                                    <div className="text-center py-8 text-gray-500">
                                                                        {t('medical_history.no_slots_available')}
                                                                    </div>
                                                                );
                                                            }
                                                            const availableDays = Object.entries(slotsData)
                                                                .map(([day, data]) => ({ day, ...data }))
                                                                .sort((a, b) => new Date(a.date) - new Date(b.date));

                                                            return (
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    {availableDays.map(({ day, date, slots }) => {
                                                                        const hasAvailableSlots = slots.filter(slot => slot.status === 'available').length > 0;
                                                                        if (!hasAvailableSlots) return null;

                                                                        return (
                                                                            <div
                                                                                key={day}
                                                                                className="group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-[0_0px_8px_0_#4ABDBD] transition-all duration-300"
                                                                            >
                                                                                <div
                                                                                    className="p-4 bg-gray-50 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-all duration-200"
                                                                                    onClick={() => toggleDayDrawerMore(day)}
                                                                                >
                                                                                    <CalendarCheck size={20} className="text-[#4ABDBD]" />
                                                                                    <h4 className="font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors truncate">
                                                                                        {format(new Date(date), 'EEEE')}, {format(new Date(date), 'MMM d, yyyy')}
                                                                                    </h4>
                                                                                </div>
                                                                                {openDayMore === day && (
                                                                                    <div className="p-4 bg-white border-t border-gray-200">
                                                                                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                                                                            {slots
                                                                                                .filter(slot => slot.status === 'available')
                                                                                                .map((slot, idx) => (
                                                                                                    <button
                                                                                                        key={idx}
                                                                                                        type="button"
                                                                                                        data-testid={`patient-medical-history-direct-slot-${day}-${idx}`}
                                                                                                        onClick={() => handleDirectSlotClickInHistory(slot, currentRecommendation)}
                                                                                                        disabled={appointmentLoading}
                                                                                                        className="p-2 rounded-xl text-center text-sm transition-all duration-200 bg-gray-50 border border-gray-200 text-gray-700 hover:bg-[#4ABDBD] hover:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                                    >
                                                                                                        {formatTime(slot.start)}
                                                                                                    </button>
                                                                                                ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : selectedTab === 'Completed' && !(userRole === 'doctor' && isEditingCompleted) ? (
                                    <>
                                        {userRole === 'doctor' && (
                                            <div className="flex justify-end mb-3 sm:mb-4">
                                                <button
                                                    type="button"
                                                    data-testid="patient-medical-history-edit-completed-button"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                    onClick={() => setIsEditingCompleted(true)}
                                                >
                                                    {t('medical_history.edit_button', 'Edit')}
                                                </button>
                                            </div>
                                        )}

                                        {/* Payment summary + (doctor/admin) mark paid / change method controls */}
                                        <CompletedPaymentCard
                                            appointment={selectedAppointment}
                                            canEdit={userRole === 'doctor' || userRole === 'admin'}
                                            onChanged={() => selectedCard.appointmentId && dispatch(getAppointmentById(selectedCard.appointmentId))}
                                        />

                                        {/* MeetingSummary now renders above for every tab. */}
                                        {/* AI appointment summary — shown once the appointment is completed */}
                                        <AppointmentSummary appointment={selectedAppointment} generationOn={aiGates.summary} className="mb-4 sm:mb-6" />

                                        {/* Every note written on this visit (empty ones skipped),
                                            including those under a section hidden or deleted since. */}
                                        {completedNoteBlocks.map((section) => (
                                            <div key={section.key} className="flex flex-col w-full gap-2 mb-4 sm:mb-6" data-testid={`patient-medical-history-completed-note-${section.key}`}>
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">{section.title}</h2>
                                                <p className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px] whitespace-pre-wrap">
                                                    {section.value}
                                                </p>
                                            </div>
                                        ))}

                                        {/* Treatment Plan – Medicines (read-only view) — hidden on preprod branch */}
                                        {visibleStaticTypes.has('medicines') && (
                                            <PrescriptionSection
                                                appointment={selectedAppointment}
                                                readOnly
                                            />
                                        )}

                                        {/* Audio Section - Multiple Clips */}
                                        {visibleStaticTypes.has('audio') && (selectedAppointment?.audioUploads?.length > 0 || selectedAppointment?.voiceClipUrl) && (
                                            <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.upload_audio_heading')}
                                                    {selectedAppointment?.audioUploads?.length > 1 && (
                                                        <span className="text-xs text-gray-400 ml-2 font-normal">({selectedAppointment.audioUploads.length})</span>
                                                    )}
                                                </h2>
                                                {selectedAppointment?.audioUploads?.length > 0 ? (
                                                    <div className="flex flex-col gap-3">
                                                        {selectedAppointment.audioUploads.map((upload, idx) => (
                                                            <div key={upload.uploadId || idx} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                                                <span className="text-xs text-gray-500 font-medium">{t('medical_history.audio_clip_label', 'Audio')} {idx + 1}</span>
                                                                <audio controls src={getFileURL(upload.audioUrl)} className="w-full mt-2" />
                                                                {audioTextBlock(upload, upload.uploadId || idx)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : selectedAppointment?.voiceClipUrl && (
                                                    <>
                                                        <audio controls src={getFileURL(selectedAppointment.voiceClipUrl)} className="w-full max-w-md" />
                                                        {selectedAppointment.transcribedNotes && (
                                                            <p className="mt-2 w-full p-3 border border-gray-300 rounded bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                                                                {selectedAppointment.transcribedNotes}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {/* Image Section - Multiple Images */}
                                        {visibleStaticTypes.has('image') && (selectedAppointment?.imageUploads?.length > 0 || selectedAppointment?.imageUrl) && (
                                            <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.uploaded_image_label')}
                                                    {selectedAppointment?.imageUploads?.length > 1 && (
                                                        <span className="text-xs text-gray-400 ml-2 font-normal">({selectedAppointment.imageUploads.length})</span>
                                                    )}
                                                </h2>
                                                {selectedAppointment?.imageUploads?.length > 0 ? (
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                        {selectedAppointment.imageUploads.map((upload, idx) => (
                                                            <img
                                                                key={upload.uploadId || idx}
                                                                src={getFileURL(upload.imageUrl)}
                                                                alt={`${t('medical_history.uploaded_image_alt')} ${idx + 1}`}
                                                                className="w-full h-auto rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                                                onClick={() => window.open(getFileURL(upload.imageUrl), '_blank')}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : selectedAppointment?.imageUrl && (
                                                    <img
                                                        src={getFileURL(selectedAppointment.imageUrl)}
                                                        alt={t('medical_history.uploaded_image_alt')}
                                                        className="w-full max-w-md h-auto rounded border border-gray-300"
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {/* OCR Notes Section - All Notes (including still-processing ones, so the spinner shows) */}
                                        {visibleStaticTypes.has('image') && (selectedAppointment?.imageUploads?.some(u => u.ocrNotes || u.status === 'processing') || selectedAppointment?.ocrNotes) && (
                                            <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.ocr_notes_label')}
                                                </h2>
                                                {selectedAppointment?.imageUploads?.length > 0 ? (
                                                    selectedAppointment.imageUploads
                                                        .filter(u => u.ocrNotes || u.status === 'processing')
                                                        .map((upload, idx) => (
                                                            <React.Fragment key={upload.uploadId || idx}>
                                                                {mediaTextBlock(upload.ocrNotes, upload.status, t('medical_history.generating_ocr', 'Generating OCR notes…'))}
                                                            </React.Fragment>
                                                        ))
                                                ) : selectedAppointment?.ocrNotes && (
                                                    <p className="w-full p-3 border border-gray-300 rounded bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px]">
                                                        {selectedAppointment.ocrNotes}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : selectedTab !== 'Cancelled' && selectedTab !== 'NoShow' && (
                                    (userRole === 'doctor' && selectedTab === 'Booked' && isWithinTwoHoursBeforeAppointment()) ||
                                    (userRole === 'doctor' && selectedTab === 'Completed' && isEditingCompleted)
                                ) ? (
                                    <>
                                        {selectedTab === 'Completed' && (
                                            <div className="flex justify-end mb-3 sm:mb-4">
                                                <button
                                                    type="button"
                                                    data-testid="patient-medical-history-cancel-edit-completed-button"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                    onClick={() => {
                                                        if (hasUnsavedChanges()) {
                                                            confirmUnsavedChanges(() => setIsEditingCompleted(false));
                                                        } else {
                                                            setIsEditingCompleted(false);
                                                        }
                                                    }}
                                                    disabled={isSaving}
                                                >
                                                    {t('medical_history.cancel_button', 'Cancel')}
                                                </button>
                                            </div>
                                        )}
                                        {userRole === 'doctor' && prescriptionFieldsLoadError && prescriptionFieldsFor === selectedAppointment?.doctorId && !prescriptionFieldsLoading && (
                                            <div role="alert" className="flex flex-wrap items-center justify-between gap-2 w-full p-3 mb-4 rounded-xl border border-red-100 bg-red-50/60" data-testid="patient-medical-history-fields-load-error">
                                                <p className="text-sm text-red-700">
                                                    {t('medical_history.fields_load_failed', 'Your note sections could not be loaded:')}{' '}
                                                    {serverReason(prescriptionFieldsLoadError, t('medical_history.fields_load_failed_hint', 'check your connection and try again.'), i18n.language)}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => dispatch(getPrescriptionFields(selectedAppointment.doctorId))}
                                                    className="px-3 py-1.5 rounded-xl border border-red-200 bg-white text-red-700 text-xs font-semibold hover:bg-red-50 cursor-pointer"
                                                    data-testid="patient-medical-history-fields-retry-button"
                                                >
                                                    {t('medical_history.retry_button', 'Retry')}
                                                </button>
                                            </div>
                                        )}
                                        {visibleNoteFields.map((f) => ({
                                            title: f.name,
                                            section: f.fieldId,
                                            value: notes[f.fieldId] ?? '',
                                        })).map((section, index) => (
                                            <div key={index} className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">{section.title}</h2>
                                                <textarea
                                                    data-testid={`patient-medical-history-notes-textarea-${section.section}`}
                                                    placeholder={t('medical_history.textarea_placeholder')}
                                                    value={section.value}
                                                    onChange={(e) => {
                                                        if (isInputDisabled()) return;

                                                        // All printable text is accepted — doses, BP 120/80,
                                                        // dates, lab values — except < and >, up to the cap.
                                                        const finalValue = cleanClinicalText(e.target.value).slice(0, MAX_NOTES_LENGTH);
                                                        setNotes((prev) => ({
                                                            ...prev,
                                                            [section.section]: finalValue
                                                        }));
                                                        // Back to the saved text → the draft is deleted
                                                        // rather than kept as a copy of the record.
                                                        noteDrafts.schedule(noteDraftSection(section.section), finalValue, {
                                                            clean: finalValue === (originalNotesRef.current?.[section.section] || ''),
                                                        });
                                                        const error = validateNotesInput(finalValue, t);
                                                        setNotesErrors((prev) => ({
                                                            ...prev,
                                                            [section.section]: error
                                                        }));
                                                    }}
                                                    disabled={isInputDisabled()}
                                                    maxLength={MAX_NOTES_LENGTH}
                                                    className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] resize-none ${expandedSections[section.section] ? 'min-h-[300px] sm:min-h-[400px]' : 'min-h-[100px]'} text-sm transition-all duration-300 ${notesErrors[section.section]
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                        }`}
                                                />
                                                {notesErrors[section.section] && (
                                                    <p className="text-red-600 text-xs mt-1">{notesErrors[section.section]}</p>
                                                )}
                                                {(userRole === 'doctor' && (selectedTab === 'Booked' || (selectedTab === 'Completed' && isEditingCompleted))) && (
                                                    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                                                        <DraftChip
                                                            section={noteDraftSection(section.section)}
                                                            chip={noteDrafts.chips[noteDraftSection(section.section)]}
                                                            onDiscard={() => discardNoteDraft(section.section)}
                                                            disabled={isSaving}
                                                        />
                                                        <button
                                                            type="button"
                                                            data-testid={`patient-medical-history-notes-expand-button-${section.section}`}
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                            onClick={() => setExpandedSections((prev) => ({ ...prev, [section.section]: !prev[section.section] }))}
                                                            disabled={isInputDisabled()}
                                                        >
                                                            {expandedSections[section.section]
                                                                ? t('medical_history.collapse_button')
                                                                : t('medical_history.expand_button')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid={`patient-medical-history-notes-save-button-${section.section}`}
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                            onClick={() => handleSaveNotes(section.section)}
                                                            disabled={isSaveButtonDisabled(`notes:${section.section}`)}
                                                        >
                                                            {savingField === `notes:${section.section}` && (
                                                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {t('medical_history.save_button')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {/* Editing a completed visit: notes under sections no longer in
                                            the template stay on screen, read-only, so nothing vanishes. */}
                                        {selectedTab === 'Completed' && completedNoteBlocks.filter((b) => !b.inTemplate).map((section) => (
                                            <div key={section.key} className="flex flex-col w-full gap-2 mb-4 sm:mb-6" data-testid={`patient-medical-history-completed-note-${section.key}`}>
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">{section.title}</h2>
                                                <p className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px] whitespace-pre-wrap">
                                                    {section.value}
                                                </p>
                                            </div>
                                        ))}
                                        {/* Treatment Plan – Medicines — hidden on preprod branch */}
                                        {visibleStaticTypes.has('medicines') && (
                                            <PrescriptionSection
                                                appointment={selectedAppointment}
                                                readOnly={isInputDisabled()}
                                                draftControlRef={prescriptionDraftRef}
                                            />
                                        )}
                                        {/* PATIENT HEALTH RECORD — the person's own cross-hospital
                                            record, written from INSIDE this appointment (the
                                            server refuses a doctor's entry that names no
                                            appointment of theirs). Round 9: a compact row here;
                                            reading and adding happen in the drawer, over the
                                            visit, so nothing typed on this screen is disturbed. */}
                                        {healthPatientId && (
                                        <HealthRecordVisitRow
                                            patientId={healthPatientId}
                                            appointmentId={selectedAppointment?.appointmentId || null}
                                            onOpen={() => setIsHealthDrawerOpen(true)}
                                        />
                                        )}
                                        {/* Audio Upload Section - Multiple Clips */}
                                        {visibleStaticTypes.has('audio') && (
                                        <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                            <div className="flex items-center justify-between">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.upload_audio_heading')}
                                                </h2>
                                                <span className="text-xs text-gray-500">
                                                    {(selectedAppointment?.audioUploads || []).length}/5
                                                </span>
                                            </div>
                                            {!canManageMedia && (
                                                <p data-testid="patient-medical-history-audio-read-only" className="text-xs text-gray-500">
                                                    {t('medical_history.media_read_only', 'Only the visit’s doctor or the hospital admin can add or remove recordings and images.')}
                                                </p>
                                            )}
                                            {canManageMedia && (selectedAppointment?.audioUploads || []).length < 5 && (
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    accept="audio/*"
                                                    onChange={handleAudioChange}
                                                    disabled={isMediaDisabled()}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    data-testid="audio-upload-input"
                                                />
                                                <div className="border-2 border-dashed border-[#4ABDBD] rounded-xl p-8 text-center bg-gradient-to-br from-[#4ABDBD]/5 to-[#3AA8A8]/5 hover:from-[#4ABDBD]/10 hover:to-[#3AA8A8]/10 transition-all duration-300 hover:border-[#3AA8A8]">
                                                    <div className="flex flex-col items-center gap-4">
                                                        <div className="w-16 h-16 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-full flex items-center justify-center">
                                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="text-base font-semibold text-[#4ABDBD] mb-1">
                                                                {t('medical_history.click_to_upload_audio')}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {t('medical_history.audio_format_hint')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            )}
                                            {audioError && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-2">{audioError}</p>
                                            )}
                                            {audioPreview && (
                                                <div className="mt-3 sm:mt-4 bg-white rounded-xl border border-gray-200 p-3">
                                                    <p className="text-xs sm:text-sm font-semibold mb-2">
                                                        {t('medical_history.audio_preview_label')}
                                                    </p>
                                                    <audio controls src={audioPreview} className="w-full max-w-md" />
                                                    <div className="flex justify-end gap-2 sm:gap-3 mt-3">
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-audio-clear-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                            onClick={() => {
                                                                setAudioFile(null);
                                                                setAudioPreview(null);
                                                                setAudioError('');
                                                            }}
                                                            disabled={isInputDisabled()}
                                                        >
                                                            {t('medical_history.clear_button')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-audio-save-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                            onClick={handleSaveAudio}
                                                            disabled={!canManageMedia || isSaveButtonDisabled('audio')}
                                                        >
                                                            {savingField === 'audio' && (
                                                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {t('medical_history.save_button')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Uploaded audio clips — player + transcript + delete */}
                                            {(selectedAppointment?.audioUploads || []).length > 0 && (
                                                <div className="mt-3 flex flex-col gap-3">
                                                    {selectedAppointment.audioUploads.map((upload, idx) => (
                                                        <div key={upload.uploadId || idx} className="relative bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-xs text-gray-500 font-medium">{t('medical_history.audio_clip_label', 'Audio')} {idx + 1}</span>
                                                                {!isMediaDisabled() && (
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`patient-medical-history-delete-audio-${upload.uploadId}`}
                                                                        onClick={() => handleDeleteAudioUpload(upload.uploadId)}
                                                                        className="text-red-500 hover:text-red-600 p-1 rounded-lg transition-all duration-200 cursor-pointer"
                                                                        title={t('medical_history.remove_audio', 'Remove')}
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <audio controls src={getFileURL(upload.audioUrl)} className="w-full" />
                                                            {audioTextBlock(upload, upload.uploadId || idx)}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Backward compatibility: old single voiceClipUrl */}
                                            {(!selectedAppointment?.audioUploads || selectedAppointment.audioUploads.length === 0) && selectedAppointment?.voiceClipUrl && (
                                                <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                                    <audio controls src={getFileURL(selectedAppointment.voiceClipUrl)} className="w-full max-w-md" />
                                                    {selectedAppointment.transcribedNotes && (
                                                        <p className="mt-2 w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                                                            {selectedAppointment.transcribedNotes}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        )}
                                        {/* Image Upload Section */}
                                        {visibleStaticTypes.has('image') && (
                                        <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                            <div className="flex items-center justify-between">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.upload_image_heading')}
                                                </h2>
                                                <span className="text-xs text-gray-500">
                                                    {(selectedAppointment?.imageUploads || []).length}/5
                                                </span>
                                            </div>

                                            {!canManageMedia && (
                                                <p data-testid="patient-medical-history-image-read-only" className="text-xs text-gray-500">
                                                    {t('medical_history.media_read_only', 'Only the visit’s doctor or the hospital admin can add or remove recordings and images.')}
                                                </p>
                                            )}
                                            {/* Upload box - hidden when 5 images reached, and for anyone
                                                but the visit's doctor and the admin (R5) */}
                                            {canManageMedia && !ocrPlanOn && (
                                                <p data-testid="patient-medical-history-image-not-in-plan" className="text-xs text-gray-500">
                                                    {t('planGate.not_in_plan_ocr', 'Reading notes from an image is not included in your plan.')}
                                                </p>
                                            )}
                                            {canManageMedia && ocrPlanOn && (selectedAppointment?.imageUploads || []).length < 5 && (
                                                <div className="relative">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                        disabled={isMediaDisabled()}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                        data-testid="image-upload-input"
                                                    />
                                                    <div className="border-2 border-dashed border-[#4ABDBD] rounded-xl p-6 text-center bg-gradient-to-br from-[#4ABDBD]/5 to-[#3AA8A8]/5 hover:from-[#4ABDBD]/10 hover:to-[#3AA8A8]/10 transition-all duration-300 hover:border-[#3AA8A8]">
                                                        <div className="flex flex-col items-center gap-3">
                                                            <div className="w-12 h-12 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-full flex items-center justify-center">
                                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-[#4ABDBD] mb-1">
                                                                    {t('medical_history.click_to_upload_image')}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {t('medical_history.image_format_hint')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {imageError && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-2">{imageError}</p>
                                            )}

                                            {/* New image preview + save/clear */}
                                            {imagePreview && (
                                                <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                                    <img
                                                        src={imagePreview}
                                                        alt={t('medical_history.image_alt')}
                                                        className="w-full max-w-md h-auto rounded-lg border border-gray-200"
                                                    />
                                                    <div className="flex justify-end gap-2 mt-3">
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-image-clear-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                            onClick={() => { setImageFile(null); setImagePreview(null); setImageError(''); }}
                                                            disabled={isInputDisabled()}
                                                        >
                                                            {t('medical_history.clear_button')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-image-save-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                            onClick={handleSaveImage}
                                                            disabled={!canManageMedia || isSaveButtonDisabled('image')}
                                                        >
                                                            {savingField === 'image' && (
                                                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {t('medical_history.save_button')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Uploaded images - small thumbnails with eye + delete */}
                                            {(selectedAppointment?.imageUploads || []).length > 0 && (
                                                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                                                    {selectedAppointment.imageUploads.map((upload, idx) => (
                                                        <div key={upload.uploadId} className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                                                            <img
                                                                src={getFileURL(upload.imageUrl)}
                                                                alt={`${t('medical_history.uploaded_image_alt')} ${idx + 1}`}
                                                                className="w-full h-20 object-cover"
                                                            />
                                                            {upload.status === 'processing' && (
                                                                <div className="absolute top-1 left-1 bg-white/90 rounded-full p-1" title={t('medical_history.generating_ocr', 'Generating OCR notes…')}>
                                                                    <span className="h-3 w-3 border-2 border-gray-300 border-t-[#4ABDBD] rounded-full animate-spin inline-block" />
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                                                <button
                                                                    type="button"
                                                                    data-testid={`patient-medical-history-view-image-${upload.uploadId}`}
                                                                    onClick={() => {
                                                                        if (detailsContainerRef.current) {
                                                                            scrollPositionRef.current = detailsContainerRef.current.scrollTop;
                                                                        }
                                                                        setViewingImageUpload(upload.uploadId);
                                                                    }}
                                                                    className="bg-white/90 hover:bg-white text-[#4ABDBD] p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                                                                    title={t('medical_history.view_image', 'View')}
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                    </svg>
                                                                </button>
                                                                {!isMediaDisabled() && (
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`patient-medical-history-delete-image-${upload.uploadId}`}
                                                                        onClick={() => handleDeleteImage(upload.uploadId)}
                                                                        className="bg-white/90 hover:bg-white text-red-500 p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                                                                        title={t('medical_history.remove_image', 'Remove')}
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="px-2 py-1 text-center">
                                                                <span className="text-[10px] text-gray-500 font-medium">{idx + 1}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Backward compatibility: old single imageUrl as thumbnail */}
                                            {(!selectedAppointment?.imageUploads || selectedAppointment.imageUploads.length === 0) && selectedAppointment?.imageUrl && (
                                                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                                                    <div className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                                                        <img
                                                            src={getFileURL(selectedAppointment.imageUrl)}
                                                            alt={t('medical_history.uploaded_image_alt')}
                                                            className="w-full h-20 object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                            <button
                                                                type="button"
                                                                data-testid="patient-medical-history-view-legacy-image-button"
                                                                onClick={() => {
                                                                    if (detailsContainerRef.current) {
                                                                        scrollPositionRef.current = detailsContainerRef.current.scrollTop;
                                                                    }
                                                                    setViewingImageUpload('legacy');
                                                                }}
                                                                className="bg-white/90 hover:bg-white text-[#4ABDBD] p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        <div className="px-2 py-1 text-center">
                                                            <span className="text-[10px] text-gray-500 font-medium">1</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        )}
                                        {(userRole === 'doctor' && selectedTab === 'Booked' && selectedAppointment.status === 'Booked') && (
                                            <div className="flex justify-end items-center gap-3 mb-4">
                                                {isPaidStatus(selectedAppointment.payment?.status) && (
                                                    <span data-testid="patient-payment-paid-badge" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-50 text-green-600 font-semibold text-sm">
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        {t('payments.paid', 'Paid')}
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    data-testid="patient-medical-history-complete-appointment-button"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center justify-center"
                                                    onClick={handleCompleteClick}
                                                    disabled={isInputDisabled() || patientLoading || appointmentLoading || committing}
                                                >
                                                    {(patientLoading || appointmentLoading) ? (
                                                        <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    ) : (
                                                        t('medical_history.complete_button')
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                    </>
                                ) : (
                                    <div className='flex items-center justify-center'>
                                        <h2 className="text-xl sm:text-4xl font-semibold text-red-500">
                                            {t(`medical_history.tabs.${selectedTab.toLowerCase()}`)}
                                        </h2>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                                <img
                                    src={medicalhistory}
                                    alt={t('medical_history.select_appointment_alt')}
                                    className="w-2/3 sm:w-1/2 md:w-1/3 lg:w-1/4 max-w-[200px] mb-4"
                                />
                                <p className="text-base sm:text-lg font-semibold text-gray-600">
                                    {t('medical_history.select_appointment_message')}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                                    {t('medical_history.select_appointment_submessage')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && (
                <div data-testid="payment-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
                    <div data-testid="payment-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
                        {/* Header Section - Sticky */}
                        <div data-testid="payment-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2 data-testid="payment-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                                {t('medical_history.invoice_heading', 'Invoice')}
                            </h2>
                            <button
                                data-testid="close-payment-modal"
                                id="btn-close-modal"
                                onClick={() => setShowPaymentModal(false)}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                            >
                                <X data-testid="close-modal-icon-payment" className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>

                        {(() => {
                            const isPayMode = paymentModalMode === 'pay';
                            const alreadyPaid = isPaidStatus(selectedAppointment?.payment?.status);
                            // Both modes: ONLY what the server returned, once it has answered —
                            // the desk methods to complete, the patient's own methods to pay.
                            const optionsAnswered = paymentOptionsStatus === 'succeeded';
                            const completeMethods = optionsAnswered
                                ? (isPayMode ? patientMethods : completeDeskMethods)
                                : [];
                            // A patient paying always needs a method; a doctor completing
                            // needs one unless the patient already paid.
                            const needsMethod = isPayMode || !alreadyPaid;
                            const optionsLoading = needsMethod && (paymentOptionsStatus === 'loading' || paymentOptionsStatus === 'idle' || !paymentOptionsStatus);
                            const optionsFailed = needsMethod && paymentOptionsStatus === 'failed';
                            const noMethodSetUp = !isPayMode && needsMethod && optionsAnswered && completeDeskMethods.length === 0;
                            // Nothing the patient may pay with here: say so and offer no Pay.
                            const noPatientPayMethod = isPayMode && optionsAnswered && patientMethods.length === 0;
                            const effectiveMethod = isPayMode ? resolvePayMethod() : resolveCompleteMethod();
                            const selectedLabel = completeMethods.find((m) => m.key === effectiveMethod)?.label
                                || (needsMethod ? (effectiveMethod ? effectiveMethod : '—') : paymentMethod);
                            const saving = patientLoading || appointmentLoading || savingField === 'payment';
                            const cannotSubmit = optionsLoading || optionsFailed || noMethodSetUp || noPatientPayMethod || (needsMethod && !effectiveMethod);
                            // In 'complete' mode the doctor doesn't re-pick a method if the
                            // patient already settled it online — just confirm completion.
                            const showMethodPicker = completeMethods.length > 1 && !(alreadyPaid && !isPayMode);
                            const submitLabel = isPayMode
                                ? `${t('medical_history.pay_with', 'Pay with')} ${selectedLabel}`
                                : (alreadyPaid
                                    ? t('medical_history.complete_appointment', 'Complete appointment')
                                    : `${t('medical_history.continue_as', 'Continue as')} ${selectedLabel}`);
                            return (
                                <>
                                    {/* Invoice body */}
                                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar space-y-4">
                                        {/* Top: payment method + status */}
                                        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
                                            <div className="flex items-center gap-2">
                                                <Wallet className="w-4 h-4 text-[#4ABDBD]" />
                                                <div>
                                                    <p className="text-[11px] text-gray-400">{t('medical_history.payment_method_heading')}</p>
                                                    <p data-testid="invoice-method" className="text-sm font-semibold text-gray-800">{selectedLabel}</p>
                                                </div>
                                            </div>
                                            <span data-testid="invoice-status" className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${alreadyPaid ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'}`}>
                                                {alreadyPaid ? t('payments.paid', 'Paid') : t('payments.not_paid', 'Not paid')}
                                            </span>
                                        </div>

                                        {/* Appointment details */}
                                        <div className="rounded-xl border border-gray-200 p-3 space-y-2">
                                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{t('medical_history.appointment_details', 'Appointment details')}</p>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">{t('medical_history.price_label')}</span>
                                                <span className="font-semibold text-gray-700">{selectedAppointment.price ? `${getCurrencySymbol(selectedAppointment.currency)}${selectedAppointment.price}` : t('medical_history.na')}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">{t('medical_history.date_label')}</span>
                                                <span className="font-semibold text-gray-700">{selectedCard.date} {selectedCard.time}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">{t('medical_history.mode_label')}</span>
                                                <span className="font-semibold text-gray-700">{selectedAppointment.appointmentMode === 'onsite' ? t('appointmentdetails.Onsite') : t('medicalHistory.visio')}</span>
                                            </div>
                                        </div>

                                        {optionsLoading && (
                                            <p data-testid="invoice-methods-loading" className="text-xs text-gray-400">
                                                {t('medical_history.invoice_methods_loading', 'Checking which payment methods this doctor accepts…')}
                                            </p>
                                        )}
                                        {optionsFailed && (
                                            <div role="alert" data-testid="invoice-methods-error" className="rounded-xl border border-red-100 bg-red-50/60 p-3 space-y-2">
                                                <p className="text-sm text-red-700">
                                                    {t('medical_history.invoice_methods_failed', 'The payment methods for this doctor could not be loaded:')}{' '}
                                                    {serverReason(paymentOptionsError, t('medical_history.invoice_methods_failed_hint', 'check your connection and try again.'), i18n.language)}
                                                </p>
                                                <button
                                                    type="button"
                                                    data-testid="invoice-methods-retry"
                                                    onClick={() => selectedAppointment?.doctorId && dispatch(getPaymentOptions(selectedAppointment.doctorId))}
                                                    className="px-3 py-1.5 rounded-xl border border-red-200 bg-white text-red-700 text-xs font-semibold hover:bg-red-50 cursor-pointer"
                                                >
                                                    {t('medical_history.retry_button', 'Retry')}
                                                </button>
                                            </div>
                                        )}
                                        {noMethodSetUp && (
                                            <div role="alert" data-testid="invoice-no-method" className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                                                <p className="text-sm font-semibold text-amber-800">
                                                    {t('medical_history.invoice_no_method_title', 'No payment method is set up')}
                                                </p>
                                                <p className="text-xs text-amber-700 mt-1">
                                                    {t('medical_history.invoice_no_method', 'This doctor accepts no payment method that can be collected here, so the visit cannot be completed yet. Set up payment first: enable a method in the hospital’s payment settings and in the doctor’s Payment & bank tab.')}
                                                </p>
                                            </div>
                                        )}
                                        {noPatientPayMethod && (
                                            <div role="status" data-testid="invoice-no-online-payment" className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                                                <p className="text-sm font-semibold text-amber-800">
                                                    {t('medical_history.invoice_no_online_payment', 'This doctor doesn’t take payment online — please pay at the hospital.')}
                                                </p>
                                            </div>
                                        )}

                                        {/* Method options — when more than one is offered (hidden once
                                            the patient has already paid and the doctor is just completing). */}
                                        {showMethodPicker && (
                                            <div data-testid="invoice-method-options">
                                                <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">{t('payments.choose_method', 'Choose a method')}</p>
                                                <div className="grid grid-cols-3 gap-2">
                                                    {completeMethods.map((m) => (
                                                        <button
                                                            key={m.key}
                                                            type="button"
                                                            onClick={() => setPaymentMethod(m.key)}
                                                            data-testid={`invoice-method-${m.key}`}
                                                            disabled={saving}
                                                            className={`text-sm px-3 py-2 rounded-xl border transition ${effectiveMethod === m.key ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8] font-semibold' : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]'}`}
                                                        >
                                                            {m.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer Section - Sticky */}
                                    <div data-testid="form-actions-container-payment" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                                        <button
                                            data-testid="cancel-button"
                                            id="btn-cancel"
                                            type="button"
                                            onClick={() => setShowPaymentModal(false)}
                                            className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                            disabled={saving}
                                        >
                                            {t('medical_history.cancel_button')}
                                        </button>
                                        {!noPatientPayMethod && (
                                        <button
                                            data-testid="pay-button"
                                            id="btn-submit"
                                            type="button"
                                            onClick={handlePaymentSubmit}
                                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                            disabled={saving || cannotSubmit}
                                        >
                                            {saving && (<span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>)}
                                            {saving ? t('medical_history.loading_appointment') : submitLabel}
                                        </button>
                                        )}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {userRole === 'doctor' && (
                <SummaryDrawer
                    isOpen={isSummaryDrawerOpen}
                    onClose={() => setIsSummaryDrawerOpen(false)}
                    patientId={selectedPatient?.patientId}
                    patientCode={hospitalPatientCode(selectedPatient)}
                    appointments={medicalHistoryData || []}
                />
            )}
            <AskAIDrawer isOpen={isAskAIDrawerOpen} onClose={() => setIsAskAIDrawerOpen(false)} />
            <HealthRecordDrawer
                isOpen={isHealthDrawerOpen && !!healthPatientId && !!draftVisitId}
                onClose={() => setIsHealthDrawerOpen(false)}
                patientId={healthPatientId}
                appointmentId={draftVisitId}
            />

        </div >
    );
};

export default MedicalHistory;